import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Sparkles,
    Send,
    Loader2,
    Megaphone,
    BarChart3,
    Check,
    AlertTriangle,
    ChevronRight,
    ChevronDown,
    Lock,
    Flame,
    Target,
    Circle,
    CheckCircle2,
    Pencil,
    UserRound,
    Headphones,
    Compass,
    Bot,
    Clock,
    ShieldCheck,
    Mic,
    Plus,
    Trash2,
    X,
    PlayCircle,
    ToggleLeft,
    ToggleRight,
    FileText,
    HelpCircle,
    ShieldAlert,
    Save,
    ArrowLeft,
    Volume2,
    Languages,
    MessageCircle,
    Layers,
    Wand2,
    Phone,
    PhoneCall,
    MicOff,
    VolumeX,
} from "lucide-react";
import { useCustomerData } from "./CustomerDataContext";
import { GROWTHOS_BASE, authHeaders } from "../config/api";

/* ==========================================================
   GROWTHOS COPILOT (id="sec-ai-assistant")

   Not a generic chatbot — this recognizes a few real commands
   ("show hot leads", "send follow-up to all") and answers them
   with REAL data from CustomerDataContext (never AI-invented
   names/scores/costs). Everything else still goes to Gemini as
   free-form chat, same as before.

   Human Approval Mode is mandatory, not optional: a bulk
   follow-up request only ever DRAFTS messages and shows them
   for review. Nothing sends until the person clicks
   "Approve & Send" — matching AI Suggests -> User Reviews ->
   User Approves -> System Executes. "Autonomous Mode" (skip
   review) is shown as a locked Pro-plan toggle, not built yet.
========================================================== */

const SEND_EMAIL_ENDPOINT = `${GROWTHOS_BASE}/send-email`;
const markContactedEndpoint = (leadId) => `${GROWTHOS_BASE}/leads/${leadId}/mark-contacted`;
// Gemini now lives server-side (main.py) so the API key never ships inside
// this JS bundle — see /ai-assistant/chat, which reuses the same
// GEMINI_API_KEY(S) KeyRotator already set up for the flyer generator.
const AI_ASSISTANT_CHAT_ENDPOINT = `${GROWTHOS_BASE}/ai-assistant/chat`;

// Same heuristic used in Lead_Management.jsx / DashboardPreviewSection.jsx —
// duplicated locally so this page doesn't depend on those files directly.
// NOT a real AI model — deterministic placeholder until a real scoring
// pipeline exists.
// Prefers the REAL ai_score column (main.py's compute_ai_score, same value
// Lead Management shows) — only falls back to this rough heuristic if a
// lead somehow has no ai_score stored yet.
const computeLeadScore = (lead) => {
    if (lead.ai_score !== null && lead.ai_score !== undefined) return Number(lead.ai_score);
    const statusWeights = { Success: 88, "Phone Contacted": 74, "Mail Send": 58, Open: 44, Closed: 28 };
    const base = statusWeights[lead.current_status] ?? 50;
    const jitter = (Number(lead.id) || 0) % 10;
    return Math.min(99, base + jitter);
};

// Finds leads mentioned by name in a (lower-cased) message — tolerant of
// spacing and small typos ("lenkscart" -> Lenskart, "tech verse" -> TechVerse).
const NAME_STOPWORDS = new Set(["call", "calls", "calling", "lead", "leads", "them", "those", "these", "show", "list", "still", "with", "please", "number", "dial", "phone", "that", "and", "the", "for", "now", "its", "only", "karo", "kar", "karna", "ko", "aur", "do", "to"]);
// words too generic to identify a company on their own (only used for the loose word-by-word match)
const GENERIC_NAME_WORDS = new Set(["solutions", "services", "digital", "technologies", "private", "limited", "india", "company"]);
const normName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const editDistance = (a, b) => {
    const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
    for (let j = 1; j <= b.length; j++) dp[0][j] = j;
    for (let i = 1; i <= a.length; i++)
        for (let j = 1; j <= b.length; j++)
            dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    return dp[a.length][b.length];
};
const findLeadsByName = (leadList, lower) => {
    const tokens = lower.split(/[^a-z0-9]+/).filter(Boolean);
    const compact = normName(lower);

    // 1) the full lead name appears in the message (spacing/punctuation ignored)
    const exact = leadList.filter((l) => {
        const f = normName(l.user_name);
        return f.length >= 3 && compact.includes(f);
    });
    if (exact.length) {
        // "TechVerse Solutions" also contains "TechVerse" — keep only the more specific match
        return exact.filter((l) => !exact.some((o) => o !== l && normName(o.user_name).includes(normName(l.user_name))));
    }

    // 2) what's left after dropping command words ("call", "them"...) is close to one lead name — typos, singular/plural
    const rest = tokens.filter((t) => !NAME_STOPWORDS.has(t)).join("");
    if (rest.length >= 4) {
        const close = leadList.filter((l) => {
            const f = normName(l.user_name);
            return f.length >= 4 && editDistance(rest, f) <= (f.length >= 8 ? 2 : 1);
        });
        if (close.length) return close;
    }

    // 3) loose: individual words ("call oyo", "call digital kraft please")
    const words = tokens.filter((w) => w.length >= 3 && !NAME_STOPWORDS.has(w));
    return leadList.filter((l) => {
        const nameWords = String(l.user_name || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length >= 3 && !NAME_STOPWORDS.has(w) && !GENERIC_NAME_WORDS.has(w));
        return words.some((w) =>
            nameWords.some((t) => w === t || (w.length >= 4 && t.length >= 4 && editDistance(w, t) <= (t.length >= 8 ? 2 : 1)))
        );
    });
};

// Turns one DB row into a compact "key=value | key=value" line for the AI's
// context. Generic on purpose: any column (name, email, phone, source, status,
// price, description...) reaches the AI without hardcoding the schema, while
// secrets (password/token/api key...) and nested objects are always dropped.
const SENSITIVE_KEY = /pass|token|secret|api_?key|hash|otp/i;
const compactRow = (row, max = 160) =>
    Object.entries(row || {})
        .filter(([k, v]) => v !== null && v !== "" && typeof v !== "object" && !SENSITIVE_KEY.test(k))
        .map(([k, v]) => `${k}=${String(v).replace(/\s+/g, " ").slice(0, max)}`)
        .join(" | ");

// Caps so the prompt stays small. Groq's free tier allows only ~8000 tokens
// PER MINUTE (request + reply), and the backend already adds business info,
// products, documents etc. — so this block gets a hard character budget.
const CONTEXT_MAX_LEADS = 40;
const CONTEXT_MAX_DEALS = 20;
const CONTEXT_MAX_CHARS = 5000; // ~1.2-1.5k tokens
const LEAD_CONTEXT_FIELDS = ["id", "user_name", "user_email", "user_mobile_number", "lead_source", "current_status", "is_contacted"];

const recommendedAction = (lead) => {
    if (!lead.is_contacted) return lead.user_email ? "Email now" : "Call now";
    if (lead.current_status === "Success") return "Onboard";
    return "Follow up again";
};

const followUpMessage = (name) =>
    `Hi ${name},\n\nJust checking in — wanted to see if you had any questions about GrowthOS AI after your last visit.\nHappy to walk you through a quick demo whenever suits you.\n\nBest,\nGrowthOS AI Team`;

const EMAIL_SUBJECT = "Following up — GrowthOS AI";

// Safety Layer: hard cap on how many emails one approval can send in a
// single batch. Real, enforced limit (used below) — not decorative.
const BATCH_CAP = 20;

// How many prior chat turns are replayed into every Gemini call. Shown in
// the Safety & Memory strip so the "short-term memory" claim is honest
// and matches what callGemini() actually does (see history.slice below).
const MEMORY_WINDOW = 10;

const chipColors = {
    orange: "bg-orange-500/15 border-orange-500/30 text-orange-400",
    blue: "bg-blue-500/15 border-blue-500/30 text-blue-400",
    green: "bg-green-500/15 border-green-500/30 text-green-400",
    purple: "bg-purple-500/15 border-purple-500/30 text-purple-400",
    rose: "bg-rose-500/15 border-rose-500/30 text-rose-400",
};

const Chip = ({ color, icon: Icon, size = 14 }) => (
    <div className={`h-7 w-7 rounded-lg border flex items-center justify-center shrink-0 ${chipColors[color]}`}>
        <Icon size={size} />
    </div>
);

const SUGGESTED_PROMPTS = [
    "Show hot leads",
    "Send follow-up to all",
    "Why did revenue drop?",
    "Deals likely to close this week",
    "Forecast this month revenue",
    "Best performing channels",
];

// Only "Increase Sales" is wired to real data + real execution right now
// (hot leads, follow-ups due, lead_source performance, actual sends via
// the backend). The rest are shown as an honest roadmap, not faked —
// they need data this app doesn't track yet (churn signals, inactivity
// windows, campaign infra).
const GOALS = [
    { label: "Increase Sales", enabled: true },
    { label: "Generate More Leads", enabled: false },
    { label: "Recover Inactive Customers", enabled: false },
    { label: "Reduce Churn", enabled: false },
];

// Multi-Agent Collaboration System. Each agent is grounded in whatever
// REAL data actually exists for its domain — leads, deals, automations.
// Sales/Marketing/Analytics map cleanly onto real tables. Support and
// Strategy are adapted: there's no support-ticket table or budget/
// competitor tracking anywhere in the schema, so those two work off the
// closest real proxy available (lead comments, pipeline/conversion
// numbers) rather than faking a ticket system or budget data.
// The AI Agents sidebar block is hidden from the UI. Every agent (runAgent /
// agentLiveStatus / AGENTS) still works — type e.g. "Run AI Sales Assistant"
// or "marketing agent" in the chat. Flip to true to show the block again.
const SHOW_AGENTS_PANEL = false;

const AGENTS = [
    {
        id: "sales",
        label: "AI Sales Assistant",
        icon: UserRound,
        color: "orange",
        description: "Predicts which deals close soonest, drafts a follow-up email",
    },
    {
        id: "marketing",
        label: "AI Marketing Engine",
        icon: Megaphone,
        color: "purple",
        description: "Finds your best channel, drafts a real campaign message",
    },
    {
        id: "support",
        label: "AI Support Agent",
        icon: Headphones,
        color: "blue",
        description: "Flags leads with open comments/questions needing a reply",
    },
    {
        id: "strategy",
        label: "AI Strategy Layer",
        icon: Compass,
        color: "green",
        description: "Growth priorities from real pipeline & conversion data",
    },
    {
        id: "analytics",
        label: "AI Analytics",
        icon: BarChart3,
        color: "rose",
        description: 'Answers "why did sales drop?", "which channel is best?"',
    },
];

const initials = (name) =>
    (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

const DEALS_ENDPOINT = `${GROWTHOS_BASE}/deals`;
const AUTOMATIONS_ENDPOINT = `${GROWTHOS_BASE}/automations`;

/* ==========================================================
   VOICE AGENTS (new tab on this same page — "sec-ai-assistant").
   Talks directly to the existing /voice-agents endpoints in
   main.py. Reuses the EXISTING /products and /knowledge-documents
   endpoints for its dropdowns — this never introduces a second
   product list or a second document-upload system. Uploading new
   Knowledge Base files still only happens from the Business
   Workspace > AI Knowledge Base tab; here an agent only SELECTS
   from documents that already exist there.
========================================================== */
const PRODUCTS_ENDPOINT = `${GROWTHOS_BASE}/products`;
const KNOWLEDGE_DOCUMENTS_ENDPOINT = `${GROWTHOS_BASE}/knowledge-documents`;
const VOICE_AGENTS_ENDPOINT = `${GROWTHOS_BASE}/voice-agents`;
const voiceAgentEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}`;
const voiceAgentActivateEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/activate`;
const voiceAgentDeactivateEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/deactivate`;
const voiceAgentQuestionsEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/questions`;
const voiceAgentQuestionsGenerateEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/questions/generate`;
const voiceAgentQuestionEndpoint = (id, qid) => `${VOICE_AGENTS_ENDPOINT}/${id}/questions/${qid}`;
const voiceAgentObjectionsEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/objections`;
const voiceAgentObjectionsGenerateEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/objections/generate`;
const voiceAgentObjectionEndpoint = (id, oid) => `${VOICE_AGENTS_ENDPOINT}/${id}/objections/${oid}`;
const voiceAgentKnowledgeEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/knowledge`;
const voiceAgentKnowledgeDocEndpoint = (id, docId) => `${VOICE_AGENTS_ENDPOINT}/${id}/knowledge/${docId}`;
const voiceAgentTestEndpoint = (id) => `${VOICE_AGENTS_ENDPOINT}/${id}/test`;

// Real outbound Twilio call — talks to POST /voice-call/start in main.py.
// No Twilio credentials ever touch this file or any React state; the
// backend places the call and only ever hands back a session id/call
// sid/status, which is all that's rendered below.
const VOICE_CALL_START_ENDPOINT = `${GROWTHOS_BASE}/voice-call/start`;
// AI drafts + saves a Voice Agent for a product that has none (POST /voice-agents/ai-create).
const VOICE_AGENT_AI_CREATE_ENDPOINT = `${VOICE_AGENTS_ENDPOINT}/ai-create`;

// GET /leads?product_id=... — scoped server-side to the logged-in
// company (via get_current_company) AND, when product_id is passed,
// to that one product (see fetch_local_leads in main.py). Used by the
// "Start a real test call" lead dropdown so it only ever offers leads
// for the product this agent is actually attached to.
const leadsByProductEndpoint = (productId) =>
    productId ? `${GROWTHOS_BASE}/leads?product_id=${productId}` : `${GROWTHOS_BASE}/leads`;

// Product Features — belongs to the PRODUCT, not the agent, so it's
// reused by every voice agent attached to that product. Reuses the
// existing /products endpoints' base; never a second product list.
const productFeaturesEndpoint = (productId) => `${GROWTHOS_BASE}/products/${productId}/features`;
const productFeatureEndpoint = (productId, featureId) => `${GROWTHOS_BASE}/products/${productId}/features/${featureId}`;
const productFeaturesGenerateEndpoint = (productId) => `${GROWTHOS_BASE}/products/${productId}/features/generate`;

const VOICE_LANGUAGE_OPTIONS = ["English", "Hindi", "Spanish", "French", "German", "Arabic", "Portuguese"];
const VOICE_OPTIONS = ["nova", "alloy", "echo", "shimmer", "onyx", "fable"];
const VOICE_TONE_OPTIONS = ["Friendly", "Professional", "Consultative", "Energetic", "Calm & Reassuring"];

const emptyVoiceAgentForm = {
    product_id: "",
    name: "",
    language: "English",
    voice: "nova",
    tone: "Friendly",
    greeting: "",
    objective: "",
    system_prompt: "",
    active: false,
};

const THEMED_SCROLLBAR =
    "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 " +
    "[&::-webkit-scrollbar-track]:bg-transparent " +
    "[&::-webkit-scrollbar-thumb]:bg-orange-500/20 [&::-webkit-scrollbar-thumb]:rounded-full " +
    "[&::-webkit-scrollbar-thumb:hover]:bg-orange-500/40 " +
    "[scrollbar-width:thin] [scrollbar-color:rgba(249,115,22,0.25)_transparent]";

const AIAssistantSection = () => {
    const navigate = useNavigate();
    const { leads, fetchLeads } = useCustomerData();

    // Which top-level view this page shows — "copilot" is everything that
    // already existed on this page (untouched below); "voice-agents" is the
    // new tab. Nothing about the Copilot chat/goals/agents logic changes
    // when this flips; it's purely which panel renders.
    const [activeTab, setActiveTab] = useState("copilot");

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef(null);

    // Full project context — fetched once on mount, refreshed after any
    // data-changing action (bulk send, goal execution) so the AI always
    // answers from CURRENT real numbers instead of guessing or only
    // knowing about leads.
    const [deals, setDeals] = useState([]);
    const [automations, setAutomations] = useState([]);
    const [products, setProducts] = useState([]);

    const fetchProjectContext = async () => {
        try {
            // authHeaders() is required here — without it these two calls
            // hit /deals and /automations with no company identity, which
            // (now that those endpoints are scoped by company_id) will
            // simply 401. Before that backend fix, omitting this header
            // was exactly what leaked every company's deals/automations
            // into every other company's AI context.
            const [dealsRes, autoRes, productsRes] = await Promise.all([
                fetch(DEALS_ENDPOINT, { method: "GET", headers: { ...authHeaders() } }),
                fetch(AUTOMATIONS_ENDPOINT, { method: "GET", headers: { ...authHeaders() } }),
                fetch(PRODUCTS_ENDPOINT, { method: "GET", headers: { ...authHeaders() } }),
            ]);
            const productsJson = await productsRes.json().catch(() => null);
            setProducts(productsRes.ok && productsJson?.success && Array.isArray(productsJson.data) ? productsJson.data : []);
            const dealsJson = await dealsRes.json().catch(() => null);
            const autoJson = await autoRes.json().catch(() => null);
            setDeals(dealsRes.ok && dealsJson?.success && Array.isArray(dealsJson.data) ? dealsJson.data : []);
            setAutomations(autoRes.ok && autoJson?.success && Array.isArray(autoJson.data) ? autoJson.data : []);
        } catch (err) {
            setDeals([]);
            setAutomations([]);
            setProducts([]);
        }
    };

    useEffect(() => {
        fetchProjectContext();
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, isLoading]);

    // Builds a compact, REAL summary of everything currently in the
    // database — leads, deals, automations — so every Gemini call (not just
    // the two special commands) is grounded in the actual project state.
    const buildProjectContext = () => {
        const hot = leads.filter((l) => computeLeadScore(l) >= 80).length;
        const due = leads.filter((l) => !l.is_contacted).length;
        const converted = leads.filter((l) => l.current_status === "Success").length;

        const wonDeals = deals.filter((d) => d.is_won);
        const openDeals = deals.filter((d) => !d.is_won);
        const pipelineValue = deals.reduce((s, d) => s + (Number(d.deal_value) || 0), 0);
        const stageCounts = {};
        deals.forEach((d) => {
            stageCounts[d.stage] = (stageCounts[d.stage] || 0) + 1;
        });
        const stageLine = Object.entries(stageCounts).map(([s, c]) => `${s}: ${c}`).join(", ") || "no deals yet";

        const runningAutomations = automations.filter((a) => a.status === "Running").length;
        const totalEnrolled = automations.reduce((s, a) => s + (Number(a.enrolled) || 0), 0);
        const totalCompleted = automations.reduce((s, a) => s + (Number(a.completed) || 0), 0);

        return (
            `=== LIVE PROJECT DATA (isfathena database, current as of this message) ===\n` +
            `Everything below belongs ONLY to the logged-in company. Answer from it, including individual lead names/emails/phones and product details when asked. Never mention or guess data of any other company.\n` +
            `LEADS (lead_generation table): ${leads.length} total leads, ${hot} hot (AI score 80+), ` +
            `${due} not yet contacted, ${converted} converted (status=Success).\n` +
            `DEALS (deals table, Sales Pipeline): ${deals.length} total deals — ${openDeals.length} open, ${wonDeals.length} closed won. ` +
            `Pipeline value: ₹${(pipelineValue / 100000).toFixed(2)}L. By stage: ${stageLine}.\n` +
            `CAMPAIGN AUTOMATIONS: ${automations.length} total workflows, ${runningAutomations} running, ` +
            `${totalEnrolled} contacts enrolled overall, ${totalCompleted} completed overall.\n` +
            // Products are NOT repeated here — the backend already folds this
            // company's products into the prompt (fetch_products).
            (() => {
                const leadLines = [...leads]
                    .sort((a, b) => computeLeadScore(b) - computeLeadScore(a)) // best leads survive the cap
                    .slice(0, CONTEXT_MAX_LEADS)
                    .map((l) => {
                        const picked = {};
                        LEAD_CONTEXT_FIELDS.forEach((k) => { picked[k] = l[k]; });
                        const prod = products.find((p) => String(p.id) === String(l.product_id));
                        return `- ${compactRow({ ...picked, ai_score: computeLeadScore(l), product: prod?.product_name }, 60)}`;
                    });
                const detail =
                    `LEAD RECORDS (top ${leadLines.length} of ${leads.length} by score):\n` +
                    (leadLines.join("\n") || "- none") + `\n` +
                    `DEAL RECORDS (top ${Math.min(deals.length, CONTEXT_MAX_DEALS)} of ${deals.length}):\n` +
                    (deals.slice(0, CONTEXT_MAX_DEALS).map((d) => `- ${compactRow(d, 60)}`).join("\n") || "- none") + `\n` +
                    `AUTOMATION RECORDS:\n` +
                    (automations.slice(0, 10).map((a) => `- ${compactRow(a, 60)}`).join("\n") || "- none") + `\n`;
                return detail.length > CONTEXT_MAX_CHARS ? detail.slice(0, CONTEXT_MAX_CHARS) + "\n...(truncated)\n" : detail;
            })() +
            `=== END LIVE DATA — use ONLY this data, never invent names, emails or numbers ===`
        );
    };

    // Calls the backend (/ai-assistant/chat in main.py) instead of Gemini
    // directly — the Gemini key + system prompt now live server-side (same
    // KeyRotator/GEMINI_API_KEY(S) pattern as the flyer generator), so the
    // key never ships inside this JS bundle. The frontend still computes
    // and sends the live project_context text + prior turns as history;
    // the backend only does the model call.
    const callGemini = async (prompt) => {
        // Real conversation memory — previous turns in THIS chat, so
        // follow-ups like "generate another one" or "I don't like this"
        // actually know what "this" refers to. Capped to the last 10 plain
        // text turns to keep the request small; structured cards (hot
        // leads table, goal plans, agent results) aren't replayable text,
        // so only kind:"text" messages are included.
        const history = messages
            .filter((m) => m.kind === "text")
            .slice(-MEMORY_WINDOW)
            .map((m) => ({ role: m.role, content: m.content }));

        const res = await fetch(AI_ASSISTANT_CHAT_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders() },
            body: JSON.stringify({
                message: prompt,
                history,
                project_context: buildProjectContext(),
            }),
        });
        if (!res.ok) throw new Error(`AI Assistant backend responded with ${res.status}`);
        const data = await res.json();
        if (!data?.success) throw new Error(data?.message || "AI Assistant is unavailable right now.");
        return data.reply || "I didn't get a usable reply — try rephrasing.";
    };

    const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);
    const updateMessage = (id, patch) =>
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));

    // Action Log — a REAL, timestamped record of things this session
    // actually did (agent runs, goal plans, sends). Client-side only:
    // it resets on page reload since there's no actions table in the
    // backend yet to persist it. Shown honestly as "This session" below.
    const [actionLog, setActionLog] = useState([]);
    const pushLog = (text) =>
        setActionLog((prev) => [
            { id: crypto.randomUUID(), time: new Date(), text },
            ...prev,
        ].slice(0, 30));

    const hotLeads = () =>
        leads
            .map((l) => ({ ...l, score: computeLeadScore(l) }))
            .filter((l) => l.score >= 80)
            .sort((a, b) => b.score - a.score);

    const dueLeads = () => leads.filter((l) => !l.is_contacted);

    const [runningAgentId, setRunningAgentId] = useState(null);

    // Each agent gets its own real-data slice + framing, then the SAME
    // callGemini() used everywhere else (which already includes the full
    // live project context) answers as that specialist.
    const runAgent = async (agentId) => {
        const agent = AGENTS.find((a) => a.id === agentId);
        if (!agent || runningAgentId) return;
        setRunningAgentId(agentId);

        let instruction = "";
        if (agentId === "sales") {
            const topHot = hotLeads()[0];
            const soonToClose = deals
                .filter((d) => !d.is_won && (d.stage === "Negotiation" || d.stage === "Proposal") && d.closing_date)
                .sort((a, b) => new Date(a.closing_date) - new Date(b.closing_date))
                .slice(0, 5)
                .map((d) => `${d.contact_name} (${d.stage}, ₹${(Number(d.deal_value) / 100000).toFixed(1)}L, closing ${d.closing_date})`)
                .join("; ") || "none in Negotiation/Proposal with a closing date set";
            instruction =
                `Act as the AI Sales Agent. Real deals nearest to closing (Negotiation/Proposal stage, sorted by closing date): ${soonToClose}.\n` +
                `Using the live lead/deal data above plus this, do 3 things:\n` +
                `1. Predict which 2-3 deals are most likely to close soonest and why (based on stage + closing date only — say plainly you're not using any probability model).\n` +
                `2. List which hot leads to prioritize today.\n` +
                (topHot
                    ? `3. Draft a short, personalized follow-up email (3-4 sentences) for ${topHot.user_name}, the single hottest lead (score ${topHot.score}). Label it clearly as "Draft email for ${topHot.user_name}:".`
                    : `3. Note there are no hot leads to draft an email for right now.`);
        } else if (agentId === "marketing") {
            const sourceCounts = {};
            leads.forEach((l) => {
                const k = l.lead_source?.trim() || "Not specified";
                sourceCounts[k] = (sourceCounts[k] || 0) + 1;
            });
            const ranked = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);
            const sourceLine = ranked.map(([s, c]) => `${s}: ${c}`).join(", ") || "no data";
            const bestChannel = ranked[0]?.[0] || "your top channel";
            instruction =
                `Act as the AI Marketing Engine. Real lead source breakdown: ${sourceLine}.\n` +
                `Do 3 things, based ONLY on this real data:\n` +
                `1. Name the best-performing channel (${bestChannel} has the most leads) and say why it's worth doubling down on.\n` +
                `2. Write one ready-to-send WhatsApp/email campaign message (4-5 sentences) aimed at generating more leads from ${bestChannel} specifically. Label it "Campaign draft:".\n` +
                `3. Suggest 2 more channel ideas worth testing next, from the ones with fewer leads.`;
        } else if (agentId === "support") {
            const withComments = leads.filter((l) => l.prospect_comment && l.prospect_comment.trim() && l.prospect_comment.trim().toLowerCase() !== "testing");
            const commentLines = withComments.slice(0, 8).map((l) => `${l.user_name}: "${l.prospect_comment}" (${l.is_contacted ? "already contacted" : "NOT yet contacted"})`).join("\n") || "No lead comments on file.";
            instruction =
                `Act as the AI Support Agent. There's no dedicated support-ticket system yet, so use each lead's saved comment as a proxy for an open question:\n${commentLines}\n\n` +
                `Say plainly that this is based on lead comments (not real tickets), then list which ones look most urgent to respond to and why. Short bullets.`;
        } else if (agentId === "strategy") {
            instruction =
                `Act as the AI Strategy Agent. There's no budget or competitor data tracked yet, so base this ONLY on the real pipeline/lead numbers above. ` +
                `Give 3 prioritized growth actions for this week, each tied to a specific real number from the data. Say plainly that budget/competitor analysis isn't available yet.`;
        } else if (agentId === "analytics") {
            instruction =
                `Act as the AI Analytics Agent. Analyze the live data above like a report: call out anything that looks like a problem (e.g. a lot of leads never contacted, ` +
                `low conversion, deals stuck in one stage), and suggest one fix for each. 4-5 short bullets.`;
        }

        pushMessage({ id: crypto.randomUUID(), role: "user", kind: "text", content: `Run ${agent.label}` });
        pushLog(`${agent.label} started`);
        try {
            const reply = await callGemini(instruction);
            pushMessage({ id: crypto.randomUUID(), role: "assistant", kind: "agent_result", agent, content: reply });
            pushLog(`${agent.label} finished — response ready`);
        } catch (err) {
            pushMessage({
                id: crypto.randomUUID(),
                role: "assistant",
                kind: "text",
                content: err?.message || "Couldn't reach the AI right now — try again in a moment.",
                isError: true,
            });
            pushLog(`${agent.label} failed — couldn't reach AI`);
        } finally {
            setRunningAgentId(null);
        }
    };

    // Same lead_source grouping used on the Analytics BI page — reused
    // here so the plan's "best channel" claim is grounded in the same
    // real counts, not a separate guess.
    const bestChannel = () => {
        if (!leads.length) return null;
        const counts = {};
        leads.forEach((l) => {
            const key = l.lead_source?.trim() || "Not specified";
            counts[key] = (counts[key] || 0) + 1;
        });
        const [top] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        return top ? { name: top[0], count: top[1] } : null;
    };

    // Live Agent Dashboard status — real counts per agent (not decorative).
    // Mirrors the doc's "AI Agents — Running / Idle" panel, but every
    // number here comes from actual state already in this component.
    const agentLiveStatus = (agentId) => {
        if (agentId === "sales") {
            const due = dueLeads().length;
            return due > 0 ? { label: "Follow-ups pending", value: due, active: true } : { label: "All caught up", value: null, active: false };
        }
        if (agentId === "marketing") {
            const running = automations.filter((a) => a.status === "Running").length;
            return running > 0
                ? { label: "Campaigns running", value: running, active: true }
                : { label: "No campaigns running", value: null, active: false };
        }
        if (agentId === "support") {
            const open = leads.filter((l) => l.prospect_comment && l.prospect_comment.trim() && l.prospect_comment.trim().toLowerCase() !== "testing").length;
            return open > 0 ? { label: "Open comments to review", value: open, active: true } : { label: "Nothing flagged", value: null, active: false };
        }
        if (agentId === "strategy") {
            const openDealsCount = deals.filter((d) => !d.is_won).length;
            return openDealsCount > 0 ? { label: "Open deals in pipeline", value: openDealsCount, active: true } : { label: "Pipeline empty", value: null, active: false };
        }
        if (agentId === "analytics") {
            return leads.length > 0 || deals.length > 0 ? { label: "Data ready to analyze", value: null, active: true } : { label: "No data yet", value: null, active: false };
        }
        return { label: "", value: null, active: false };
    };

    const startGoal = (goalLabel) => {
        const hot = hotLeads();
        const due = dueLeads();
        const channel = bestChannel();

        pushMessage({ id: crypto.randomUUID(), role: "user", kind: "text", content: `Goal: ${goalLabel}` });
        pushLog(`Orchestrator started goal "${goalLabel}"`);

        const findings = [
            `${hot.length} hot leads available`,
            `${due.length} follow-up${due.length !== 1 ? "s" : ""} pending`,
            channel ? `${channel.name} is your best-performing source (${channel.count} leads)` : null,
        ].filter(Boolean);

        const planTargets = due.slice(0, BATCH_CAP);

        pushMessage({
            id: crypto.randomUUID(),
            role: "assistant",
            kind: "goal_plan",
            goal: goalLabel,
            findings,
            routedVia: [
                "AI Sales Assistant",
                channel ? "AI Marketing Engine" : null,
            ].filter(Boolean),
            steps: [
                { label: `Draft follow-up emails for ${planTargets.length} pending leads`, done: false },
                { label: "Review & approve messages", done: false },
                { label: "Send and update CRM", done: false },
            ],
            drafts: planTargets.map((l) => ({
                id: l.id,
                name: l.user_name || "there",
                email: l.user_email,
                message: followUpMessage(l.user_name || "there"),
            })),
            status: "planning", // planning | executing | done
            sentCount: 0,
        });
    };

    // ---- Voice input (mic) -------------------------------------------
    // Browser Web Speech API: speech -> text (free, no backend/API key).
    // The transcript goes through the SAME handleSend() as typed text, so
    // hot-leads / follow-up / Gemini (/ai-assistant/chat) all work by voice.
    // Reply is read aloud (speechSynthesis) only for voice-originated msgs.
    const VOICE_LANG = "en-IN"; // try "hi-IN" for Hindi-heavy speech
    const [isListening, setIsListening] = useState(false);
    const [voiceReply, setVoiceReply] = useState(true);
    const recognitionRef = useRef(null);
    const handleSendRef = useRef(null);
    const replyByVoiceRef = useRef(false);

    const speak = (text) => {
        if (typeof window === "undefined" || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(String(text).replace(/[*_`#>]/g, ""));
        u.lang = VOICE_LANG;
        window.speechSynthesis.speak(u);
    };

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            return;
        }
        const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!Ctor) {
            pushMessage({
                id: crypto.randomUUID(),
                role: "assistant",
                kind: "text",
                content: "Voice input isn't supported in this browser — please use Chrome or Edge.",
                isError: true,
            });
            return;
        }
        window.speechSynthesis?.cancel(); // stop AI speaking when user starts talking
        const rec = new Ctor();
        rec.lang = VOICE_LANG;
        rec.interimResults = true;
        rec.continuous = false;
        rec.onresult = (e) => {
            let t = "";
            for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
            setInput(t); // live transcript shows in the input bar
            if (e.results[e.results.length - 1].isFinal && t.trim()) {
                replyByVoiceRef.current = true;
                handleSendRef.current?.(t); // always the latest handleSend (fresh state)
            }
        };
        rec.onerror = () => setIsListening(false);
        rec.onend = () => setIsListening(false);
        recognitionRef.current = rec;
        rec.start();
        setIsListening(true);
    };

    useEffect(() => () => {
        recognitionRef.current?.abort();
        window.speechSynthesis?.cancel();
    }, []);

    // ---- Calling: "call <lead>" / "call all (hot) leads" -------------------
    // Resolves targets + phone + product + active Voice Agent from real DB
    // data, shows a plan card, and ONLY places calls after "Approve & Call"
    // (same human-approval rule as emails). The live call itself is the
    // existing Voice Agent pipeline: POST /voice-call/start -> Twilio ->
    // the agent speaks + listens, with product/lead/knowledge-base context.
    const CALL_BATCH_CAP = 5;
    const lastLeadsRef = useRef([]); // leads from the last list card / last lead mentioned — for "call them", "still call"
    const pendingCallRef = useRef(false); // true right after asking "which lead?", so a bare lead name works as the answer

    const planCalls = async (lower, viaVoice) => {
        const say = (content, isError) =>
            pushMessage({ id: crypto.randomUUID(), role: "assistant", kind: "text", content, isError });

        // Any lead can be called — not just hot ones. Order: "hot leads" / "all leads" keywords,
        // then a lead named in the message (typo-tolerant), then whoever was just listed or mentioned
        // ("call them", "still do call", just "call").
        let targets;
        const named = findLeadsByName(leads, lower);
        if (lower.includes("hot")) {
            targets = hotLeads();
        } else if (/\b(all|every|everyone|sab|sabhi)\b/.test(lower)) {
            targets = leads;
        } else if (named.length) {
            targets = named;
        } else {
            targets = lastLeadsRef.current;
        }

        if (targets.length === 0) {
            pendingCallRef.current = true; // next message naming a lead is the answer
            say('Which lead should I call? Say a lead\'s name, "hot leads" or "all leads".');
            return;
        }
        lastLeadsRef.current = targets;
        const hasPhone = (l) => (l.user_mobile_number || "").trim().length > 0;
        const callable = targets.filter(hasPhone);
        const noNumber = targets.filter((l) => !hasPhone(l));
        if (callable.length === 0) {
            say(`No phone number on file for ${noNumber.map((l) => l.user_name).join(", ")} — add a mobile number first.`, true);
            return;
        }
        const picked = callable.slice(0, CALL_BATCH_CAP);

        let allAgents = [];
        try {
            const res = await fetch(VOICE_AGENTS_ENDPOINT, { headers: authHeaders() });
            const json = await res.json().catch(() => null);
            if (res.ok && json?.success && Array.isArray(json.data)) allAgents = json.data;
        } catch (e) {
            /* handled below */
        }
        const agents = allAgents.filter((a) => a.active !== false);

        const productOf = (id) => products.find((p) => String(p.id) === String(id));

        // A lead that belongs to a product needs an ACTIVE agent built for THAT product.
        // If there isn't one, stop here and ask first (AI can create it) — never call with a mismatched agent.
        const missing = new Map(); // product_id -> product name (no agent at all)
        const inactive = new Map(); // product_id -> product name (agent exists but is deactivated)
        const planned = [];
        let noGenericAgent = false;
        picked.forEach((l) => {
            let agent;
            if (l.product_id != null) {
                const forProduct = allAgents.filter((a) => String(a.product_id) === String(l.product_id));
                agent = forProduct.find((a) => a.active !== false);
                if (!agent) {
                    const pname = productOf(l.product_id)?.product_name || `product #${l.product_id}`;
                    (forProduct.length ? inactive : missing).set(l.product_id, pname);
                    return;
                }
            } else {
                agent = agents.find((a) => a.product_id == null) || agents[0];
                if (!agent) {
                    noGenericAgent = true;
                    return;
                }
            }
            planned.push({ l, agent });
        });

        if (inactive.size) {
            say(
                `A voice agent for ${[...inactive.values()].join(", ")} exists but is deactivated — activate it in the Voice Agents tab, then ask me again.`,
                true
            );
            return;
        }
        if (missing.size) {
            pushMessage({
                id: crypto.randomUUID(),
                role: "assistant",
                kind: "agent_missing",
                status: "ask", // ask | creating | done | error
                products: [...missing.entries()].map(([id, name]) => ({ id: Number(id), name })),
                lower,
            });
            if (viaVoice && voiceReply) speak("There's no voice agent for this product yet. Should I create one with AI first?");
            return;
        }
        if (noGenericAgent || planned.length === 0) {
            say("No active Voice Agent found — create or activate one in the Voice Agents tab first.", true);
            return;
        }

        const calls = planned.map(({ l, agent }) => {
            const prod = productOf(agent.product_id ?? l.product_id);
            return {
                leadId: l.id,
                name: l.user_name || "Lead",
                phone: l.user_mobile_number,
                agentId: agent.id,
                agentName: agent.name,
                productName: prod?.product_name || "",
                productInfo: [prod?.price ? `price: ${prod.price}` : "", prod?.description ? String(prod.description).slice(0, 100) : ""]
                    .filter(Boolean)
                    .join(", "),
                product: prod,
            };
        });

        // Preview of what the agent may additionally say (AI-generated, informational —
        // the live agent itself works from its own prompt + product + knowledge base).
        let extras = "";
        const distinct = [...new Map(calls.filter((c) => c.product).map((c) => [c.product.id, c.product])).values()].slice(0, 3);
        if (distinct.length) {
            try {
                extras = await callGemini(
                    `I'm about to place sales calls for: ${distinct
                        .map((p) => `${p.product_name} (${p.price || "no price set"}; ${String(p.description || "").slice(0, 150)})`)
                        .join(" | ")}. For each product give 2 short extra talking points (one line each, plain text) the voice agent could additionally mention. Use only the facts given — don't invent prices or guarantees.`
                );
            } catch (e) {
                extras = "";
            }
        }

        pushMessage({
            id: crypto.randomUUID(),
            role: "assistant",
            kind: "call_plan",
            status: "draft", // draft | calling | done
            calls: calls.map(({ product, ...c }) => c),
            skipped: noNumber.map((l) => l.user_name || "Lead"),
            overCap: callable.length - picked.length,
            extras,
            results: [],
        });
        pushLog(`Planned ${calls.length} AI voice call${calls.length !== 1 ? "s" : ""} — awaiting approval`);
        if (viaVoice && voiceReply) speak(`I can call ${calls.length} lead${calls.length !== 1 ? "s" : ""}. Please approve to start.`);
    };

    // "Create with AI & continue" on the agent_missing card: AI drafts + saves one
    // Voice Agent per missing product, then the call plan is rebuilt (still needs approval).
    const createAgentsAndContinue = async (messageId) => {
        const msg = messages.find((m) => m.id === messageId);
        if (!msg) return;
        updateMessage(messageId, { status: "creating" });
        const created = [];
        try {
            for (const p of msg.products) {
                const res = await fetch(VOICE_AGENT_AI_CREATE_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({ product_id: p.id }),
                });
                const data = await res.json().catch(() => null);
                if (!(res.ok && data?.success)) {
                    throw new Error(data?.message || data?.detail || `Couldn't create an agent for ${p.name}`);
                }
                created.push(`${p.name}: ${data.message || "voice agent created"}`);
            }
            updateMessage(messageId, { status: "done" });
            pushMessage({
                id: crypto.randomUUID(),
                role: "assistant",
                kind: "text",
                content:
                    `Done — ${created.join("; ")}.\n` +
                    "Everything was AI-generated from your product and company details, so please review it. " +
                    "You can change any of it as you prefer in the Voice Agents tab (greeting, questions, objections, features, knowledge documents).",
            });
            pushLog(`AI created voice agent${msg.products.length !== 1 ? "s" : ""} for ${msg.products.map((p) => p.name).join(", ")}`);
            setIsLoading(true);
            try {
                await planCalls(msg.lower, false);
            } finally {
                setIsLoading(false);
            }
        } catch (err) {
            updateMessage(messageId, { status: "error", error: err?.message || "Couldn't create the voice agent." });
        }
    };

    // Only fires when the person clicks "Approve & Call" — never automatically.
    const approveCalls = async (messageId) => {
        const msg = messages.find((m) => m.id === messageId);
        if (!msg) return;
        updateMessage(messageId, { status: "calling" });
        const results = [];
        for (const c of msg.calls) {
            try {
                const res = await fetch(VOICE_CALL_START_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({ lead_id: Number(c.leadId), agent_id: Number(c.agentId) }),
                });
                const data = await res.json().catch(() => null);
                const ok = res.ok && !!data?.success;
                results.push({
                    leadId: c.leadId,
                    ok,
                    text: ok ? `Call placed (${data.status || "initiated"})` : data?.message || data?.detail || "Couldn't start the call",
                });
            } catch (err) {
                results.push({ leadId: c.leadId, ok: false, text: "Couldn't reach the server to start the call" });
            }
            updateMessage(messageId, { results: [...results] });
        }
        updateMessage(messageId, { status: "done", results });
        pushLog(`Placed ${results.filter((r) => r.ok).length} of ${results.length} AI voice calls`);
    };

    const handleSend = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || isLoading) return;
        const viaVoice = replyByVoiceRef.current;
        replyByVoiceRef.current = false;
        // AI Agents panel is hidden from the sidebar, but each agent is still
        // runnable by name from the chat (runAgent posts its own "Run ..." message).
        const agentMatch = AGENTS.find((a) => {
            const l = text.toLowerCase();
            return l.includes(a.label.toLowerCase()) || l.includes(`${a.id} agent`);
        });
        if (agentMatch) {
            setInput("");
            runAgent(agentMatch.id);
            return;
        }
        setInput("");
        pushMessage({ id: crypto.randomUUID(), role: "user", kind: "text", content: text });

        const lower = text.toLowerCase();

        // remember which lead(s) the person just talked about, so "still do call" / "call them" know who
        const mentioned = findLeadsByName(leads, lower);
        if (mentioned.length) lastLeadsRef.current = mentioned;

        // ---- Calling command: plan card, calls only after approval ----
        if (
            ((/\bcall\b|\bdial\b|कॉल/.test(lower)) &&
                !/\b(history|log|logs|session|sessions|transcript|summary)\b/.test(lower)) ||
            (pendingCallRef.current && mentioned.length > 0)
        ) {
            pendingCallRef.current = false;
            setIsLoading(true);
            try {
                await planCalls(lower, viaVoice);
            } finally {
                setIsLoading(false);
            }
            return;
        }

        // ---- Real-data command: hot leads table ----
        if (lower.includes("hot lead")) {
            const top = hotLeads(); // show every hot lead, not just a top-5 sample
            lastLeadsRef.current = top;
            pushMessage({ id: crypto.randomUUID(), role: "assistant", kind: "hot_leads", leads: top });
            pushLog(`Looked up hot leads — ${top.length} found`);
            if (viaVoice && voiceReply) speak(`I found ${top.length} hot leads.`);
            return;
        }

        // ---- Real-data command: bulk follow-up (draft-only, needs approval) ----
        if (lower.includes("follow-up") || lower.includes("follow up")) {
            const targets = dueLeads().slice(0, BATCH_CAP); // sane cap for one batch
            if (targets.length === 0) {
                pushMessage({
                    id: crypto.randomUUID(),
                    role: "assistant",
                    kind: "text",
                    content: "Every lead has already been contacted at least once — nothing due for follow-up right now.",
                });
                return;
            }
            pushMessage({
                id: crypto.randomUUID(),
                role: "assistant",
                kind: "approval",
                status: "draft", // draft | reviewing | sending | done
                drafts: targets.map((l) => ({
                    id: l.id,
                    name: l.user_name || "there",
                    email: l.user_email,
                    message: followUpMessage(l.user_name || "there"),
                })),
                sentCount: 0,
            });
            pushLog(`Drafted ${targets.length} follow-up email${targets.length !== 1 ? "s" : ""} — awaiting approval`);
            return;
        }

        // ---- Real-data command: full lead list (names + emails), no AI involved ----
        if (/\bleads?\b/.test(lower) && /\b(show|list|all|name|names|email|emails|details?)\b/.test(lower)) {
            const all = leads
                .map((l) => ({ ...l, score: computeLeadScore(l) }))
                .sort((a, b) => b.score - a.score);
            lastLeadsRef.current = all;
            pushMessage({ id: crypto.randomUUID(), role: "assistant", kind: "hot_leads", leads: all, label: "lead" });
            pushLog(`Listed all leads — ${all.length} found`);
            if (viaVoice && voiceReply) speak(`You have ${all.length} leads.`);
            return;
        }

        // ---- Everything else: free-form Gemini chat, via the backend ----
        // (no client-side "is it configured" check anymore — the Gemini
        // key lives server-side now; if it's missing, /ai-assistant/chat
        // returns a clean success:false message that the catch below shows.)
        setIsLoading(true);
        try {
            const reply = await callGemini(text);
            pushMessage({ id: crypto.randomUUID(), role: "assistant", kind: "text", content: reply });
            if (viaVoice && voiceReply) speak(reply);
        } catch (err) {
            pushMessage({
                id: crypto.randomUUID(),
                role: "assistant",
                kind: "text",
                content: err?.message || "Couldn't reach the AI right now — try again in a moment.",
                isError: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    handleSendRef.current = handleSend;

    // Only fires when the person explicitly clicks "Approve & Send" —
    // never automatically. This is the one place real sends happen.
    const approveAndSend = async (messageId) => {
        const msg = messages.find((m) => m.id === messageId);
        if (!msg) return;
        updateMessage(messageId, { status: "sending" });

        let sentCount = 0;
        for (const draft of msg.drafts) {
            if (!draft.email) continue;
            try {
                const res = await fetch(SEND_EMAIL_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        to_email: draft.email,
                        to_name: draft.name,
                        subject: EMAIL_SUBJECT,
                        message: draft.message,
                    }),
                });
                const data = await res.json().catch(() => null);
                if (res.ok && data?.success) {
                    sentCount++;
                    fetch(markContactedEndpoint(draft.id), { method: "POST", headers: authHeaders() }).catch(() => {});
                }
            } catch (err) {
                // this one lead failed — continue with the rest of the batch
            }
        }

        fetchLeads();
        fetchProjectContext(); // keep the AI's live-data context in sync
        updateMessage(messageId, { status: "done", sentCount });
        pushLog(`Approved & sent ${sentCount} of ${msg.drafts.length} follow-up emails`);
    };

    // Executes a goal plan: same real send + mark-contacted as
    // approveAndSend, but also advances the step checklist so the
    // person can watch progress (Phase 6/9 idea, kept lightweight —
    // no persisted task queue, just this message's own state).
    const approveGoalPlan = async (messageId) => {
        const msg = messages.find((m) => m.id === messageId);
        if (!msg) return;

        updateMessage(messageId, {
            status: "executing",
            steps: msg.steps.map((s, i) => (i <= 1 ? { ...s, done: true } : s)),
        });
        pushLog(`Executing goal "${msg.goal}" — sending ${msg.drafts.length} draft${msg.drafts.length !== 1 ? "s" : ""}`);

        let sentCount = 0;
        for (const draft of msg.drafts) {
            if (!draft.email) continue;
            try {
                const res = await fetch(SEND_EMAIL_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        to_email: draft.email,
                        to_name: draft.name,
                        subject: EMAIL_SUBJECT,
                        message: draft.message,
                    }),
                });
                const data = await res.json().catch(() => null);
                if (res.ok && data?.success) {
                    sentCount++;
                    fetch(markContactedEndpoint(draft.id), { method: "POST", headers: authHeaders() }).catch(() => {});
                }
            } catch (err) {
                // this one lead failed — continue with the rest of the batch
            }
        }

        fetchLeads();
        fetchProjectContext(); // keep the AI's live-data context in sync
        setMessages((prev) =>
            prev.map((m) =>
                m.id === messageId
                    ? { ...m, status: "done", sentCount, steps: m.steps.map((s) => ({ ...s, done: true })) }
                    : m
            )
        );
        pushLog(`Goal "${msg.goal}" done — sent ${sentCount} of ${msg.drafts.length}, CRM updated`);
    };

    return (
        <section id="sec-ai-assistant" className={`h-full flex flex-col overflow-y-auto lg:overflow-hidden relative z-10 py-3 sm:py-4 ${THEMED_SCROLLBAR}`}>
            <div className="w-full flex flex-col px-4 sm:px-6 lg:px-8 pb-4 lg:pb-0 lg:h-full lg:min-h-0">
                <button
                    type="button"
                    onClick={() => navigate("/app/dashboard-preview")}
                    className="shrink-0 self-start mb-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-600/40 hover:bg-orange-600/10 transition"
                >
                    <ArrowLeft size={15} /> Back
                </button>
                <div className="flex items-center justify-between shrink-0 flex-wrap gap-2">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
                            GrowthOS Copilot <Sparkles size={20} className="text-orange-500" />
                        </h2>
                        <p className="text-gray-400 text-sm mt-1">Your AI partner for growth.</p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {activeTab === "copilot" && (
                            <div className="flex items-center gap-2 text-[11px] text-gray-500 border border-white/10 rounded-lg px-3 py-2">
                                <ShieldCheck size={13} className="text-green-500 shrink-0" />
                                <span>
                                    Human Approval: <span className="text-gray-300 font-semibold">ON</span> · Batch cap:{" "}
                                    <span className="text-gray-300 font-semibold">{BATCH_CAP}</span> · Memory:{" "}
                                    <span className="text-gray-300 font-semibold">last {MEMORY_WINDOW} msgs</span>
                                </span>
                            </div>
                        )}
                        {activeTab === "copilot" && (
                            <button
                                disabled
                                title="Autonomous Mode — Pro plan, coming soon"
                                className="flex items-center gap-2 border border-white/10 text-gray-500 px-3 py-2 rounded-lg text-xs font-semibold cursor-not-allowed"
                            >
                                <Lock size={12} /> Autonomous Mode
                            </button>
                        )}
                    </div>
                </div>

                {/* Tab switcher — Copilot (existing chat/goals/agents page,
                    untouched) vs. Voice Agents (new). */}
                <div className="flex items-center gap-2 mt-3 shrink-0 flex-wrap">
                    <button
                        onClick={() => setActiveTab("copilot")}
                        className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border transition ${
                            activeTab === "copilot"
                                ? "bg-orange-500 border-orange-500 text-black"
                                : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
                        }`}
                    >
                        <Sparkles size={13} /> Copilot
                    </button>
                    <button
                        onClick={() => setActiveTab("voice-agents")}
                        className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border transition ${
                            activeTab === "voice-agents"
                                ? "bg-orange-500 border-orange-500 text-black"
                                : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
                        }`}
                    >
                        <Mic size={13} /> Voice Agents
                    </button>
                </div>

                {activeTab === "voice-agents" ? (
                    <div className={`mt-4 min-h-[480px] lg:min-h-0 lg:flex-1 bg-black border border-orange-600/30 rounded-2xl sm:rounded-[28px] overflow-hidden overflow-y-auto ${THEMED_SCROLLBAR}`}>
                        <VoiceAgentsPanel />
                    </div>
                ) : (
                <div className="mt-4 lg:flex-1 lg:min-h-0 bg-black border border-orange-600/30 rounded-2xl sm:rounded-[28px] overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-3 lg:h-full">
                        {/* Chat */}
                        <div className="lg:col-span-2 lg:border-r border-orange-600/20 h-[70vh] min-h-[420px] max-h-[720px] lg:h-full lg:max-h-none lg:min-h-0 flex flex-col">
                            <div ref={scrollRef} className={`flex-1 min-h-0 overflow-y-auto px-3 sm:px-5 py-4 sm:py-5 space-y-4 ${THEMED_SCROLLBAR}`}>
                                {messages.length === 0 && (
                                    <div className="bg-black border border-orange-600/30 rounded-2xl p-4 max-w-[90%]">
                                        <p className="text-gray-300 text-sm leading-6">
                                            Ask me anything, or try "Show hot leads" / "Send follow-up to all" for
                                            answers grounded in your real lead data.
                                        </p>
                                    </div>
                                )}

                                {messages.map((m) => (
                                    <ChatMessage
                                        key={m.id}
                                        message={m}
                                        onApprove={() => approveAndSend(m.id)}
                                        onApproveGoal={() => approveGoalPlan(m.id)}
                                        onApproveCalls={() => approveCalls(m.id)}
                                        onCreateAgents={() => createAgentsAndContinue(m.id)}
                                    />
                                ))}

                                {isLoading && (
                                    <div className="bg-black border border-orange-600/30 rounded-2xl p-3 w-fit flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin text-orange-500" />
                                        <span className="text-xs text-gray-400">Thinking...</span>
                                    </div>
                                )}
                            </div>

                            <div className="px-3 sm:px-5 py-3 sm:py-4 border-t border-orange-600/20 shrink-0">
                                <div className="flex items-center gap-2 bg-white/5 border border-white/10 focus-within:border-orange-600/50 rounded-xl px-3 py-2.5 transition">
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                        placeholder={isListening ? "Listening... speak now" : "Ask anything or type a command..."}
                                        className="flex-1 min-w-0 bg-transparent outline-none text-sm text-white placeholder:text-gray-500"
                                    />
                                    <button
                                        onClick={() => setVoiceReply((v) => !v)}
                                        title={voiceReply ? "AI voice reply: ON" : "AI voice reply: OFF"}
                                        className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center shrink-0 transition"
                                    >
                                        {voiceReply ? <Volume2 size={15} className="text-orange-500" /> : <VolumeX size={15} className="text-gray-500" />}
                                    </button>
                                    <button
                                        onClick={toggleListening}
                                        disabled={isLoading}
                                        title={isListening ? "Stop listening" : "Speak"}
                                        className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 transition disabled:opacity-40 ${
                                            isListening ? "bg-red-500 animate-pulse" : "bg-white/10 hover:bg-white/20"
                                        }`}
                                    >
                                        {isListening ? <MicOff size={15} className="text-white" /> : <Mic size={15} className="text-orange-500" />}
                                    </button>
                                    <button
                                        onClick={() => handleSend()}
                                        disabled={isLoading || !input.trim()}
                                        className="h-8 w-8 rounded-lg bg-orange-500 disabled:bg-orange-500/30 disabled:cursor-not-allowed hover:bg-orange-600 flex items-center justify-center shrink-0 transition"
                                    >
                                        <Send size={15} className="text-black" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className={`bg-black border-t lg:border-t-0 border-orange-600/40 text-white p-4 sm:p-5 lg:h-full lg:min-h-0 flex flex-col overflow-y-auto ${THEMED_SCROLLBAR}`}>
                            <h3 className="text-sm font-bold text-white shrink-0 flex items-center gap-1.5">
                                <Target size={14} className="text-orange-500" /> Goals
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5 shrink-0">What do you want to achieve?</p>
                            <div className="space-y-2 mt-3 shrink-0">
                                {GOALS.map((g) => (
                                    <button
                                        key={g.label}
                                        onClick={() => g.enabled && startGoal(g.label)}
                                        disabled={!g.enabled}
                                        title={g.enabled ? undefined : "Coming soon — needs data this app doesn't track yet"}
                                        className={`w-full text-left rounded-xl px-3 py-2.5 text-sm transition duration-200 border ${
                                            g.enabled
                                                ? "bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20 text-white"
                                                : "bg-white/[0.02] border-white/5 text-gray-600 cursor-not-allowed"
                                        }`}
                                    >
                                        {g.label} {!g.enabled && <Lock size={10} className="inline ml-1 mb-0.5" />}
                                    </button>
                                ))}
                            </div>

                            <h3 className="text-sm font-bold text-white mt-6 shrink-0">Suggested Prompts</h3>
                            <div className="space-y-2 mt-3 shrink-0">
                                {SUGGESTED_PROMPTS.map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => handleSend(p)}
                                        disabled={isLoading}
                                        className="w-full text-left bg-white/10 hover:bg-orange-500/15 border border-transparent hover:border-orange-500/40 rounded-xl px-3 py-2.5 text-sm transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>

                            {SHOW_AGENTS_PANEL && (
                            <>
                            <h3 className="text-sm font-bold text-white mt-6 shrink-0 flex items-center gap-1.5">
                                <Bot size={14} className="text-orange-500" /> AI Agents
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5 shrink-0">Specialists grounded in your real data</p>
                            <div className="space-y-2 mt-3 shrink-0">
                                {AGENTS.map((agent) => {
                                    const Icon = agent.icon;
                                    const running = runningAgentId === agent.id;
                                    const live = agentLiveStatus(agent.id);
                                    return (
                                        <button
                                            key={agent.id}
                                            onClick={() => runAgent(agent.id)}
                                            disabled={Boolean(runningAgentId)}
                                            className="w-full text-left bg-white/5 hover:bg-orange-500/10 border border-white/10 hover:border-orange-500/30 rounded-xl p-2.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Chip color={agent.color} icon={Icon} size={13} />
                                                <span className="text-xs font-semibold text-white flex-1">{agent.label}</span>
                                                {running && <Loader2 size={12} className="animate-spin text-orange-400 shrink-0" />}
                                            </div>
                                            <p className="text-[10px] text-gray-500 mt-1.5 leading-snug">{agent.description}</p>
                                            <div className="flex items-center gap-1.5 mt-1.5">
                                                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${running ? "bg-orange-400 animate-pulse" : live.active ? "bg-green-400" : "bg-gray-600"}`} />
                                                <span className="text-[10px] text-gray-400">
                                                    {running ? "Running..." : live.value !== null ? `${live.label}: ${live.value}` : live.label}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            </>
                            )}

                            <h3 className="text-sm font-bold text-white mt-6 shrink-0 flex items-center gap-1.5">
                                <Clock size={14} className="text-orange-500" /> Action Log
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5 shrink-0">This session — resets on reload</p>
                            <div className={`mt-3 shrink-0 max-h-40 overflow-y-auto space-y-1.5 pr-1 ${THEMED_SCROLLBAR}`}>
                                {actionLog.length === 0 ? (
                                    <p className="text-[11px] text-gray-600 italic">No actions taken yet</p>
                                ) : (
                                    actionLog.map((entry) => (
                                        <div key={entry.id} className="flex items-start gap-2 text-[11px]">
                                            <span className="text-gray-600 shrink-0 tabular-nums">
                                                {entry.time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </span>
                                            <span className="text-gray-300 leading-snug">{entry.text}</span>
                                        </div>
                                    ))
                                )}
                            </div>

                            <h3 className="text-sm font-bold text-white mt-6 shrink-0">Quick Actions</h3>
                            <div className="space-y-2 mt-3 shrink-0">
                                <button
                                    onClick={() => navigate("/app/campaign-automation", { state: { openCreateAutomation: true } })}
                                    className="w-full flex items-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3 py-2.5 text-sm transition"
                                >
                                    <Megaphone size={15} className="text-orange-500" /> Create Campaign
                                </button>
                                <button
                                    onClick={() => navigate("/app/analytics-bi")}
                                    className="w-full flex items-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3 py-2.5 text-sm transition"
                                >
                                    <BarChart3 size={15} className="text-orange-500" /> Analyze Leads
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>
        </section>
    );
};

/* ==========================================================
   VOICE AGENTS PANEL — new tab on this page. List -> Create/Edit
   -> (once saved) Qualification Questions / Objection Handling /
   Knowledge Base selection / Test Agent. Every call is scoped to
   the logged-in company via authHeaders(), same as the rest of
   this file — a company only ever sees/edits its own agents.
========================================================== */
const VoiceAgentsPanel = () => {
    const [agents, setAgents] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [view, setView] = useState("list"); // list | edit
    const [activeAgent, setActiveAgent] = useState(null); // full agent row once created/selected

    const loadAgents = async () => {
        setLoading(true);
        setError("");
        try {
            const [agentsRes, productsRes] = await Promise.all([
                fetch(VOICE_AGENTS_ENDPOINT, { headers: authHeaders() }),
                fetch(PRODUCTS_ENDPOINT, { headers: authHeaders() }),
            ]);
            const agentsJson = await agentsRes.json().catch(() => null);
            const productsJson = await productsRes.json().catch(() => null);
            setAgents(agentsRes.ok && agentsJson?.success ? agentsJson.data : []);
            setProducts(productsRes.ok && productsJson?.success ? productsJson.data : []);
        } catch (err) {
            setError("Could not load voice agents right now.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAgents();
    }, []);

    const openCreate = () => {
        setActiveAgent(null);
        setView("edit");
    };

    const openEdit = (agent) => {
        setActiveAgent(agent);
        setView("edit");
    };

    const backToList = (refresh) => {
        setView("list");
        setActiveAgent(null);
        if (refresh) loadAgents();
    };

    const toggleActive = async (agent) => {
        const endpoint = agent.active ? voiceAgentDeactivateEndpoint(agent.id) : voiceAgentActivateEndpoint(agent.id);
        try {
            const res = await fetch(endpoint, { method: "POST", headers: authHeaders() });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setAgents((prev) => prev.map((a) => (a.id === agent.id ? data.data[0] : a)));
            }
        } catch (err) {
            // leave the list as-is — the button itself reflects true state on next load
        }
    };

    const deleteAgent = async (agent) => {
        if (!window.confirm(`Delete "${agent.name}"? This can't be undone.`)) return;
        try {
            const res = await fetch(voiceAgentEndpoint(agent.id), { method: "DELETE", headers: authHeaders() });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setAgents((prev) => prev.filter((a) => a.id !== agent.id));
            }
        } catch (err) {
            // no-op — list simply won't update; person can retry
        }
    };

    if (view === "edit") {
        return (
            <VoiceAgentEditor
                agent={activeAgent}
                products={products}
                onBack={backToList}
            />
        );
    }

    return (
        <div className={`h-full min-h-0 overflow-y-auto p-4 sm:p-6 ${THEMED_SCROLLBAR}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Mic size={17} className="text-orange-500" /> Voice Agents
                    </h3>
                    <p className="text-gray-400 text-xs mt-1">
                        Configure AI voice agents per product — greeting, tone, qualification questions, objection
                        handling, and which Knowledge Base documents they can use.
                    </p>
                </div>
                <button
                    onClick={openCreate}
                    className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-black text-xs font-bold px-3.5 py-2.5 rounded-xl transition shrink-0"
                >
                    <Plus size={14} /> New Voice Agent
                </button>
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-xl px-3.5 py-2.5 mb-4">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-10 justify-center">
                    <Loader2 size={16} className="animate-spin" /> Loading voice agents...
                </div>
            ) : agents.length === 0 ? (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center text-gray-400 text-sm">
                    No voice agents yet. Create one to give a product its own AI voice sales rep.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {agents.map((agent) => {
                        const product = products.find((p) => p.id === agent.product_id);
                        return (
                            <div
                                key={agent.id}
                                className="bg-white/5 border border-white/10 hover:border-orange-500/30 rounded-2xl p-4 transition"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-white truncate">{agent.name}</p>
                                        <p className="text-[11px] text-gray-500 truncate">
                                            {product ? product.product_name : "No product selected"}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => toggleActive(agent)}
                                        title={agent.active ? "Deactivate" : "Activate"}
                                        className={`flex items-center gap-1 shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full border ${
                                            agent.active
                                                ? "bg-green-500/10 border-green-500/30 text-green-400"
                                                : "bg-white/5 border-white/10 text-gray-400"
                                        }`}
                                    >
                                        {agent.active ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                                        {agent.active ? "Active" : "Inactive"}
                                    </button>
                                </div>

                                <div className="flex items-center flex-wrap gap-1.5 mt-3">
                                    <span className="flex items-center gap-1 text-[10px] text-gray-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
                                        <Languages size={10} /> {agent.language}
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] text-gray-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
                                        <Volume2 size={10} /> {agent.voice}
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] text-gray-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
                                        {agent.tone}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2 mt-3.5">
                                    <button
                                        onClick={() => openEdit(agent)}
                                        className="flex items-center gap-1 flex-1 justify-center bg-white/10 hover:bg-white/15 text-white text-xs font-semibold px-3 py-2 rounded-xl transition"
                                    >
                                        <Pencil size={12} /> Manage
                                    </button>
                                    <button
                                        onClick={() => deleteAgent(agent)}
                                        title="Delete agent"
                                        className="flex items-center justify-center bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-2 rounded-xl transition"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ==========================================================
   Create/Edit Voice Agent — basic config form. Qualification
   Questions / Objection Handling / Knowledge Base / Test Agent
   only appear once the agent has a real id (i.e. after the first
   Save), since all four are nested under /voice-agents/{agent_id}.
========================================================== */
const VoiceAgentEditor = ({ agent, products, onBack }) => {
    const [form, setForm] = useState(
        agent
            ? {
                  product_id: agent.product_id ?? "",
                  name: agent.name ?? "",
                  language: agent.language ?? "English",
                  voice: agent.voice ?? "nova",
                  tone: agent.tone ?? "Friendly",
                  greeting: agent.greeting ?? "",
                  objective: agent.objective ?? "",
                  system_prompt: agent.system_prompt ?? "",
                  active: Boolean(agent.active),
              }
            : emptyVoiceAgentForm
    );
    const [savedAgent, setSavedAgent] = useState(agent || null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [savedMsg, setSavedMsg] = useState("");

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    // Drives the Product Features panel below — reactive to the Product
    // dropdown so switching products immediately swaps which feature
    // list is shown, even before Save Changes is clicked.
    const selectedProductId = form.product_id ? Number(form.product_id) : null;
    const selectedProduct = products.find((p) => p.id === selectedProductId);

    const handleSave = async () => {
        if (!form.name.trim()) {
            setError("Give the agent a name first.");
            return;
        }
        setSaving(true);
        setError("");
        setSavedMsg("");
        try {
            const isEdit = Boolean(savedAgent?.id);
            const endpoint = isEdit ? voiceAgentEndpoint(savedAgent.id) : VOICE_AGENTS_ENDPOINT;
            const method = isEdit ? "PUT" : "POST";
            const body = {
                product_id: form.product_id === "" ? null : Number(form.product_id),
                name: form.name.trim(),
                language: form.language,
                voice: form.voice,
                tone: form.tone,
                greeting: form.greeting,
                objective: form.objective,
                system_prompt: form.system_prompt,
                active: form.active,
            };
            const res = await fetch(endpoint, {
                method,
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                throw new Error(data?.message || "Could not save this voice agent.");
            }
            setSavedAgent(data.data[0]);
            setSavedMsg(isEdit ? "Saved" : "Agent created — now add questions, objections, and knowledge below.");
        } catch (err) {
            setError(err?.message || "Could not save this voice agent.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`h-full min-h-0 overflow-y-auto p-4 sm:p-6 ${THEMED_SCROLLBAR}`}>
            <button
                onClick={() => onBack(true)}
                className="flex items-center gap-1.5 text-gray-400 hover:text-white text-xs font-semibold mb-4"
            >
                <ArrowLeft size={13} /> Back to Voice Agents
            </button>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <Mic size={16} className="text-orange-500" />
                        {savedAgent?.id ? "Edit Voice Agent" : "New Voice Agent"}
                    </h3>
                    {savedAgent?.id && (
                        <span
                            className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border ${
                                form.active
                                    ? "bg-green-500/10 border-green-500/30 text-green-400"
                                    : "bg-white/5 border-white/10 text-gray-400"
                            }`}
                        >
                            {form.active ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                            {form.active ? "Active" : "Inactive"}
                        </span>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <Field label="Agent Name">
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setField("name", e.target.value)}
                            placeholder="e.g. Aria — Solar Sales Agent"
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                        />
                    </Field>

                    <Field label="Product">
                        <select
                            value={form.product_id}
                            onChange={(e) => setField("product_id", e.target.value)}
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                        >
                            <option value="">No product selected</option>
                            {products.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.product_name}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Language">
                        <select
                            value={form.language}
                            onChange={(e) => setField("language", e.target.value)}
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                        >
                            {VOICE_LANGUAGE_OPTIONS.map((l) => (
                                <option key={l} value={l}>
                                    {l}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Voice">
                        <select
                            value={form.voice}
                            onChange={(e) => setField("voice", e.target.value)}
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                        >
                            {VOICE_OPTIONS.map((v) => (
                                <option key={v} value={v}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Tone">
                        <select
                            value={form.tone}
                            onChange={(e) => setField("tone", e.target.value)}
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                        >
                            {VOICE_TONE_OPTIONS.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Call Objective">
                        <input
                            type="text"
                            value={form.objective}
                            onChange={(e) => setField("objective", e.target.value)}
                            placeholder="e.g. Book a demo call"
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                        />
                    </Field>

                    <Field label="Greeting" full>
                        <textarea
                            value={form.greeting}
                            onChange={(e) => setField("greeting", e.target.value)}
                            rows={2}
                            placeholder="Hi, this is Aria calling from..."
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
                        />
                    </Field>

                    <Field label="Extra Instructions (optional)" full>
                        <textarea
                            value={form.system_prompt}
                            onChange={(e) => setField("system_prompt", e.target.value)}
                            rows={2}
                            placeholder="Anything else this agent should always keep in mind..."
                            className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
                        />
                    </Field>
                </div>

                {error && <p className="text-xs text-red-400 mt-3">{error}</p>}
                {savedMsg && <p className="text-xs text-green-400 mt-3">{savedMsg}</p>}

                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black text-xs font-bold px-4 py-2.5 rounded-xl transition mt-4"
                >
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {savedAgent?.id ? "Save Changes" : "Create Agent"}
                </button>
            </div>

            {savedAgent?.id && (
                <div className="mt-5 space-y-5">
                    <QuestionsEditor agentId={savedAgent.id} />
                    <ObjectionsEditor agentId={savedAgent.id} />
                    <ProductFeaturesEditor
                        productId={selectedProductId}
                        productName={selectedProduct?.product_name}
                    />
                    <KnowledgeSelector agentId={savedAgent.id} />
                    <TestAgentPanel
                        agentId={savedAgent.id}
                        productId={selectedProductId}
                        productName={selectedProduct?.product_name}
                    />
                </div>
            )}
        </div>
    );
};

const Field = ({ label, children, full }) => (
    <label className={`flex flex-col gap-1.5 ${full ? "sm:col-span-2" : ""}`}>
        <span className="text-[11px] text-gray-500 font-semibold">{label}</span>
        {children}
    </label>
);

/* ==========================================================
   Qualification Questions — this agent's own Q&A knowledge base
   (voice_agent_questions), separate from the company-wide
   Knowledge Base documents below.
========================================================== */
const QuestionsEditor = ({ agentId }) => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({ question: "", answer: "", category: "", priority: 0 });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState("");
    const [addingIndex, setAddingIndex] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetch(voiceAgentQuestionsEndpoint(agentId), { headers: authHeaders() });
            const data = await res.json().catch(() => null);
            setQuestions(res.ok && data?.success ? data.data : []);
        } catch (err) {
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        setSuggestions([]);
        setGenError("");
        setSaveError("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    // Returns true/false so callers (addDraft / addSuggestion / addAll)
    // know whether to also clear their own local state — a failed save
    // must NEVER silently disappear a suggestion the user still needs.
    const addQuestion = async (payload) => {
        if (!payload.question.trim() || !payload.answer.trim()) return false;
        setSaving(true);
        setSaveError("");
        try {
            const res = await fetch(voiceAgentQuestionsEndpoint(agentId), {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setQuestions((prev) => [data.data[0], ...prev]);
                return true;
            }
            setSaveError(data?.message || `Could not save this question (server said: ${res.status} ${res.statusText}).`);
            return false;
        } catch (err) {
            setSaveError("Could not reach the server to save this question. Check your connection and try again.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const addDraft = async () => {
        const ok = await addQuestion(draft);
        if (ok) setDraft({ question: "", answer: "", category: "", priority: 0 });
    };

    const removeQuestion = async (q) => {
        try {
            const res = await fetch(voiceAgentQuestionEndpoint(agentId, q.id), {
                method: "DELETE",
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) setQuestions((prev) => prev.filter((x) => x.id !== q.id));
        } catch (err) {
            // no-op
        }
    };

    const generateWithAI = async () => {
        if (!agentId) return;
        setGenerating(true);
        setGenError("");
        try {
            const res = await fetch(voiceAgentQuestionsGenerateEndpoint(agentId), {
                method: "POST",
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setSuggestions(data.data || []);
                if (!data.data?.length) setGenError("AI didn't return any suggestions — try again.");
            } else {
                setGenError(data?.message || `Could not generate questions (server said: ${res.status} ${res.statusText}).`);
            }
        } catch (err) {
            setGenError("Could not reach AI generation.");
        } finally {
            setGenerating(false);
        }
    };

    const addSuggestion = async (s, idx) => {
        setAddingIndex(idx);
        const ok = await addQuestion({
            question: s.question,
            answer: s.answer || "",
            category: s.category || "general",
            priority: s.priority || 0,
        });
        if (ok) setSuggestions((prev) => prev.filter((_, i) => i !== idx));
        setAddingIndex(null);
    };

    const addAllSuggestions = async () => {
        const toAdd = [...suggestions];
        const stillFailed = [];
        for (let i = 0; i < toAdd.length; i++) {
            // eslint-disable-next-line no-await-in-loop
            const ok = await addQuestion({
                question: toAdd[i].question,
                answer: toAdd[i].answer || "",
                category: toAdd[i].category || "general",
                priority: toAdd[i].priority || 0,
            });
            if (!ok) stillFailed.push(toAdd[i]);
        }
        // Only clear the ones that actually saved — anything that failed
        // stays on screen so the user can see it and retry.
        setSuggestions(stillFailed);
    };

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h4 className="text-sm font-bold text-white flex items-center gap-1.5">
                    <HelpCircle size={15} className="text-orange-500" /> Qualification Questions
                </h4>
                <button
                    onClick={generateWithAI}
                    disabled={generating}
                    className="flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 disabled:opacity-60 text-orange-400 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition"
                >
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                    Generate with AI
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <input
                    type="text"
                    value={draft.question}
                    onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))}
                    placeholder="Question (e.g. What's your budget?)"
                    className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
                <input
                    type="text"
                    value={draft.category}
                    onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                    placeholder="Category (optional)"
                    className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
                <textarea
                    value={draft.answer}
                    onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))}
                    placeholder="Answer to use / what to listen for"
                    rows={2}
                    className="sm:col-span-2 w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
                />
            </div>
            <button
                onClick={addDraft}
                disabled={saving || !draft.question.trim() || !draft.answer.trim()}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition mt-2.5"
            >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Add Question
            </button>

            {saveError && <p className="text-xs text-red-400 mt-2.5">{saveError}</p>}
            {genError && <p className="text-xs text-red-400 mt-3">{genError}</p>}

            {suggestions.length > 0 && (
                <div className="mt-4 bg-orange-500/5 border border-orange-500/20 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-[11px] font-semibold text-orange-400 flex items-center gap-1.5">
                            <Sparkles size={12} /> AI Suggestions — review before adding
                        </p>
                        <button
                            onClick={addAllSuggestions}
                            disabled={saving}
                            className="text-[10px] font-semibold text-orange-400 hover:text-orange-300 underline disabled:opacity-50"
                        >
                            Add all
                        </button>
                    </div>
                    {saveError && <p className="text-[11px] text-red-400 mb-2">{saveError}</p>}
                    <div className="space-y-2">
                        {suggestions.map((s, idx) => (
                            <div
                                key={idx}
                                className="flex items-start justify-between gap-2 bg-black border border-white/10 rounded-xl p-2.5"
                            >
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-white">
                                        {s.question}{" "}
                                        {s.category && <span className="text-gray-500 font-normal">· {s.category}</span>}
                                    </p>
                                    {s.answer && <p className="text-[11px] text-gray-400 mt-0.5">{s.answer}</p>}
                                </div>
                                <button
                                    onClick={() => addSuggestion(s, idx)}
                                    disabled={addingIndex === idx}
                                    className="shrink-0 flex items-center gap-1 text-[10px] font-semibold text-orange-400 hover:text-orange-300 transition"
                                >
                                    {addingIndex === idx ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                                    Add
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-4 space-y-2">
                {loading ? (
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <Loader2 size={12} className="animate-spin" /> Loading...
                    </p>
                ) : questions.length === 0 ? (
                    <p className="text-xs text-gray-500">No qualification questions added yet.</p>
                ) : (
                    questions.map((q) => (
                        <div key={q.id} className="flex items-start justify-between gap-2 bg-black border border-white/10 rounded-xl p-2.5">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-white">
                                    {q.question} {q.category && <span className="text-gray-500 font-normal">· {q.category}</span>}
                                </p>
                                <p className="text-[11px] text-gray-400 mt-0.5">{q.answer}</p>
                            </div>
                            <button
                                onClick={() => removeQuestion(q)}
                                className="shrink-0 text-gray-500 hover:text-red-400 transition"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

/* ==========================================================
   Objection Handling scripts (voice_agent_objections).
========================================================== */
const ObjectionsEditor = ({ agentId }) => {
    const [objections, setObjections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({ objection_type: "", response_script: "", success_rate: 0 });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState("");
    const [addingIndex, setAddingIndex] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetch(voiceAgentObjectionsEndpoint(agentId), { headers: authHeaders() });
            const data = await res.json().catch(() => null);
            setObjections(res.ok && data?.success ? data.data : []);
        } catch (err) {
            setObjections([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        setSuggestions([]);
        setGenError("");
        setSaveError("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    // Returns true/false so callers (addDraft / addSuggestion / addAll)
    // know whether to also clear their own local state — a failed save
    // must NEVER silently disappear a suggestion the user still needs.
    const addObjection = async (payload) => {
        if (!payload.objection_type.trim() || !payload.response_script.trim()) return false;
        setSaving(true);
        setSaveError("");
        try {
            const res = await fetch(voiceAgentObjectionsEndpoint(agentId), {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setObjections((prev) => [data.data[0], ...prev]);
                return true;
            }
            setSaveError(data?.message || `Could not save this objection (server said: ${res.status} ${res.statusText}).`);
            return false;
        } catch (err) {
            setSaveError("Could not reach the server to save this objection. Check your connection and try again.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const addDraft = async () => {
        const ok = await addObjection(draft);
        if (ok) setDraft({ objection_type: "", response_script: "", success_rate: 0 });
    };

    const removeObjection = async (o) => {
        try {
            const res = await fetch(voiceAgentObjectionEndpoint(agentId, o.id), {
                method: "DELETE",
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) setObjections((prev) => prev.filter((x) => x.id !== o.id));
        } catch (err) {
            // no-op
        }
    };

    const generateWithAI = async () => {
        if (!agentId) return;
        setGenerating(true);
        setGenError("");
        try {
            const res = await fetch(voiceAgentObjectionsGenerateEndpoint(agentId), {
                method: "POST",
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setSuggestions(data.data || []);
                if (!data.data?.length) setGenError("AI didn't return any suggestions — try again.");
            } else {
                setGenError(data?.message || `Could not generate objections (server said: ${res.status} ${res.statusText}).`);
            }
        } catch (err) {
            setGenError("Could not reach AI generation.");
        } finally {
            setGenerating(false);
        }
    };

    const addSuggestion = async (s, idx) => {
        setAddingIndex(idx);
        const ok = await addObjection({
            objection_type: s.objection_type,
            response_script: s.response_script || "",
            success_rate: 0,
        });
        if (ok) setSuggestions((prev) => prev.filter((_, i) => i !== idx));
        setAddingIndex(null);
    };

    const addAllSuggestions = async () => {
        const toAdd = [...suggestions];
        const stillFailed = [];
        for (let i = 0; i < toAdd.length; i++) {
            // eslint-disable-next-line no-await-in-loop
            const ok = await addObjection({
                objection_type: toAdd[i].objection_type,
                response_script: toAdd[i].response_script || "",
                success_rate: 0,
            });
            if (!ok) stillFailed.push(toAdd[i]);
        }
        // Only clear the ones that actually saved — anything that failed
        // stays on screen so the user can see it and retry.
        setSuggestions(stillFailed);
    };

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h4 className="text-sm font-bold text-white flex items-center gap-1.5">
                    <ShieldAlert size={15} className="text-orange-500" /> Objection Handling
                </h4>
                <button
                    onClick={generateWithAI}
                    disabled={generating}
                    className="flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 disabled:opacity-60 text-orange-400 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition"
                >
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                    Generate with AI
                </button>
            </div>

            <div className="grid grid-cols-1 gap-2.5">
                <input
                    type="text"
                    value={draft.objection_type}
                    onChange={(e) => setDraft((d) => ({ ...d, objection_type: e.target.value }))}
                    placeholder="Objection (e.g. &quot;It's too expensive&quot;)"
                    className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
                <textarea
                    value={draft.response_script}
                    onChange={(e) => setDraft((d) => ({ ...d, response_script: e.target.value }))}
                    placeholder="How the agent should respond"
                    rows={2}
                    className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
                />
            </div>
            <button
                onClick={addDraft}
                disabled={saving || !draft.objection_type.trim() || !draft.response_script.trim()}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition mt-2.5"
            >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Add Objection Script
            </button>

            {saveError && <p className="text-xs text-red-400 mt-2.5">{saveError}</p>}
            {genError && <p className="text-xs text-red-400 mt-3">{genError}</p>}

            {suggestions.length > 0 && (
                <div className="mt-4 bg-orange-500/5 border border-orange-500/20 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-[11px] font-semibold text-orange-400 flex items-center gap-1.5">
                            <Sparkles size={12} /> AI Suggestions — review before adding
                        </p>
                        <button
                            onClick={addAllSuggestions}
                            disabled={saving}
                            className="text-[10px] font-semibold text-orange-400 hover:text-orange-300 underline disabled:opacity-50"
                        >
                            Add all
                        </button>
                    </div>
                    {saveError && <p className="text-[11px] text-red-400 mb-2">{saveError}</p>}
                    <div className="space-y-2">
                        {suggestions.map((s, idx) => (
                            <div
                                key={idx}
                                className="flex items-start justify-between gap-2 bg-black border border-white/10 rounded-xl p-2.5"
                            >
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-white">"{s.objection_type}"</p>
                                    {s.response_script && <p className="text-[11px] text-gray-400 mt-0.5">{s.response_script}</p>}
                                </div>
                                <button
                                    onClick={() => addSuggestion(s, idx)}
                                    disabled={addingIndex === idx}
                                    className="shrink-0 flex items-center gap-1 text-[10px] font-semibold text-orange-400 hover:text-orange-300 transition"
                                >
                                    {addingIndex === idx ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                                    Add
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-4 space-y-2">
                {loading ? (
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <Loader2 size={12} className="animate-spin" /> Loading...
                    </p>
                ) : objections.length === 0 ? (
                    <p className="text-xs text-gray-500">No objection scripts added yet.</p>
                ) : (
                    objections.map((o) => (
                        <div key={o.id} className="flex items-start justify-between gap-2 bg-black border border-white/10 rounded-xl p-2.5">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-white">"{o.objection_type}"</p>
                                <p className="text-[11px] text-gray-400 mt-0.5">{o.response_script}</p>
                            </div>
                            <button
                                onClick={() => removeObjection(o)}
                                className="shrink-0 text-gray-500 hover:text-red-400 transition"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

/* ==========================================================
   Product Features — this PRODUCT's own feature list
   (company_product_features), scoped to whichever product is
   currently selected above, not to the agent itself — so it's the
   same list no matter which agent for that product you're editing.
   Manual add/delete, plus "Generate with AI" which asks Groq for a
   draft list; nothing from AI is saved until the user adds a
   suggestion individually (or hits "Add all").
========================================================== */
const ProductFeaturesEditor = ({ productId, productName }) => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({ feature_name: "", description: "", category: "" });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState("");
    const [addingIndex, setAddingIndex] = useState(null);

    const load = async () => {
        if (!productId) {
            setFeatures([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(productFeaturesEndpoint(productId), { headers: authHeaders() });
            const data = await res.json().catch(() => null);
            setFeatures(res.ok && data?.success ? data.data : []);
        } catch (err) {
            setFeatures([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        setSuggestions([]);
        setGenError("");
        setSaveError("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    // Returns true/false so callers (addDraft / addSuggestion / addAll)
    // know whether to also clear their own local state (e.g. remove a
    // suggestion from the list) — a failed save must NEVER silently
    // disappear a suggestion the user still needs to retry.
    const addFeature = async (payload) => {
        if (!productId || !payload.feature_name.trim()) return false;
        setSaving(true);
        setSaveError("");
        try {
            const res = await fetch(productFeaturesEndpoint(productId), {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setFeatures((prev) => [data.data[0], ...prev]);
                return true;
            }
            setSaveError(
                data?.message ||
                    `Could not save this feature (server said: ${res.status} ${res.statusText}). If this is the ` +
                        "first feature you're adding, the company_product_features table may not exist yet — see the " +
                        "setup note below."
            );
            return false;
        } catch (err) {
            setSaveError("Could not reach the server to save this feature. Check your connection and try again.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const addDraft = async () => {
        const ok = await addFeature(draft);
        if (ok) setDraft({ feature_name: "", description: "", category: "" });
    };

    const removeFeature = async (f) => {
        try {
            const res = await fetch(productFeatureEndpoint(productId, f.id), {
                method: "DELETE",
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) setFeatures((prev) => prev.filter((x) => x.id !== f.id));
        } catch (err) {
            // no-op
        }
    };

    const generateWithAI = async () => {
        if (!productId) return;
        setGenerating(true);
        setGenError("");
        try {
            const res = await fetch(productFeaturesGenerateEndpoint(productId), {
                method: "POST",
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setSuggestions(data.data || []);
                if (!data.data?.length) setGenError("AI didn't return any suggestions — try again.");
            } else {
                setGenError(data?.message || `Could not generate features (server said: ${res.status} ${res.statusText}).`);
            }
        } catch (err) {
            setGenError("Could not reach AI generation.");
        } finally {
            setGenerating(false);
        }
    };

    const addSuggestion = async (s, idx) => {
        setAddingIndex(idx);
        const ok = await addFeature({
            feature_name: s.feature_name,
            description: s.description || "",
            category: s.category || "General",
        });
        if (ok) setSuggestions((prev) => prev.filter((_, i) => i !== idx));
        setAddingIndex(null);
    };

    const addAllSuggestions = async () => {
        const toAdd = [...suggestions];
        const stillFailed = [];
        for (let i = 0; i < toAdd.length; i++) {
            // eslint-disable-next-line no-await-in-loop
            const ok = await addFeature({
                feature_name: toAdd[i].feature_name,
                description: toAdd[i].description || "",
                category: toAdd[i].category || "General",
            });
            if (!ok) stillFailed.push(toAdd[i]);
        }
        // Only clear the ones that actually saved — anything that failed
        // stays on screen so the user can see it and retry.
        setSuggestions(stillFailed);
    };

    if (!productId) {
        return (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
                <h4 className="text-sm font-bold text-white flex items-center gap-1.5 mb-1">
                    <Layers size={15} className="text-orange-500" /> Product Features
                </h4>
                <p className="text-[11px] text-gray-500">Select a product above to manage its feature list.</p>
            </div>
        );
    }

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <h4 className="text-sm font-bold text-white flex items-center gap-1.5">
                    <Layers size={15} className="text-orange-500" /> Product Features
                </h4>
                <button
                    onClick={generateWithAI}
                    disabled={generating}
                    className="flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 disabled:opacity-60 text-orange-400 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition"
                >
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                    Generate with AI
                </button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
                {productName ? `Features of ${productName}` : "Features of this product"} — give the agent (and anyone
                reading the product's info) accurate details to describe on a call.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <input
                    type="text"
                    value={draft.feature_name}
                    onChange={(e) => setDraft((d) => ({ ...d, feature_name: e.target.value }))}
                    placeholder="Feature (e.g. Real-time sync)"
                    className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
                <input
                    type="text"
                    value={draft.category}
                    onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                    placeholder="Category (optional)"
                    className="w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
                <textarea
                    value={draft.description}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                    placeholder="What it does / why it matters (optional)"
                    rows={2}
                    className="sm:col-span-2 w-full bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"
                />
            </div>
            <button
                onClick={addDraft}
                disabled={saving || !draft.feature_name.trim()}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition mt-2.5"
            >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Add Feature
            </button>

            {saveError && <p className="text-xs text-red-400 mt-2.5">{saveError}</p>}
            {genError && <p className="text-xs text-red-400 mt-3">{genError}</p>}

            {suggestions.length > 0 && (
                <div className="mt-4 bg-orange-500/5 border border-orange-500/20 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-[11px] font-semibold text-orange-400 flex items-center gap-1.5">
                            <Sparkles size={12} /> AI Suggestions — review before adding
                        </p>
                        <button
                            onClick={addAllSuggestions}
                            disabled={saving}
                            className="text-[10px] font-semibold text-orange-400 hover:text-orange-300 underline disabled:opacity-50"
                        >
                            Add all
                        </button>
                    </div>
                    {saveError && <p className="text-[11px] text-red-400 mb-2">{saveError}</p>}
                    <div className="space-y-2">
                        {suggestions.map((s, idx) => (
                            <div
                                key={idx}
                                className="flex items-start justify-between gap-2 bg-black border border-white/10 rounded-xl p-2.5"
                            >
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-white">
                                        {s.feature_name}{" "}
                                        {s.category && <span className="text-gray-500 font-normal">· {s.category}</span>}
                                    </p>
                                    {s.description && <p className="text-[11px] text-gray-400 mt-0.5">{s.description}</p>}
                                </div>
                                <button
                                    onClick={() => addSuggestion(s, idx)}
                                    disabled={addingIndex === idx}
                                    className="shrink-0 flex items-center gap-1 text-[10px] font-semibold text-orange-400 hover:text-orange-300 transition"
                                >
                                    {addingIndex === idx ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                                    Add
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-4 space-y-2">
                {loading ? (
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <Loader2 size={12} className="animate-spin" /> Loading...
                    </p>
                ) : features.length === 0 ? (
                    <p className="text-xs text-gray-500">No features added yet for this product.</p>
                ) : (
                    features.map((f) => (
                        <div key={f.id} className="flex items-start justify-between gap-2 bg-black border border-white/10 rounded-xl p-2.5">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-white">
                                    {f.feature_name} {f.category && <span className="text-gray-500 font-normal">· {f.category}</span>}
                                </p>
                                {f.description && <p className="text-[11px] text-gray-400 mt-0.5">{f.description}</p>}
                            </div>
                            <button
                                onClick={() => removeFeature(f)}
                                className="shrink-0 text-gray-500 hover:text-red-400 transition"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

/* ==========================================================
   Knowledge Base selector — lets this agent pick from documents
   that already exist in the company's AI Knowledge Base tab
   (/knowledge-documents). This never uploads a file itself and
   never creates a second document store; it only links/unlinks
   an EXISTING document_id via /voice-agents/{id}/knowledge.
========================================================== */
const KnowledgeSelector = ({ agentId }) => {
    const [allDocs, setAllDocs] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [allRes, selectedRes] = await Promise.all([
                fetch(KNOWLEDGE_DOCUMENTS_ENDPOINT, { headers: authHeaders() }),
                fetch(voiceAgentKnowledgeEndpoint(agentId), { headers: authHeaders() }),
            ]);
            const allJson = await allRes.json().catch(() => null);
            const selectedJson = await selectedRes.json().catch(() => null);
            setAllDocs(allRes.ok && allJson?.success ? allJson.data : []);
            setSelectedIds(
                new Set(selectedRes.ok && selectedJson?.success ? selectedJson.data.map((d) => d.id) : [])
            );
        } catch (err) {
            setAllDocs([]);
            setSelectedIds(new Set());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    const toggleDoc = async (doc) => {
        const isSelected = selectedIds.has(doc.id);
        setBusyId(doc.id);
        try {
            const res = await fetch(voiceAgentKnowledgeDocEndpoint(agentId, doc.id), {
                method: isSelected ? "DELETE" : "POST",
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (isSelected) next.delete(doc.id);
                    else next.add(doc.id);
                    return next;
                });
            }
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
            <h4 className="text-sm font-bold text-white flex items-center gap-1.5 mb-1">
                <FileText size={15} className="text-orange-500" /> Knowledge Base Documents
            </h4>
            <p className="text-[11px] text-gray-500 mb-3">
                Select which documents from your company's AI Knowledge Base this agent can use to answer factual
                questions. Upload new files from Business Workspace &gt; AI Knowledge Base.
            </p>

            {loading ? (
                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin" /> Loading...
                </p>
            ) : allDocs.length === 0 ? (
                <p className="text-xs text-gray-500">No Knowledge Base documents uploaded yet for this company.</p>
            ) : (
                <div className={`space-y-2 max-h-64 overflow-y-auto pr-1 ${THEMED_SCROLLBAR}`}>
                    {allDocs.map((doc) => {
                        const selected = selectedIds.has(doc.id);
                        return (
                            <button
                                key={doc.id}
                                onClick={() => toggleDoc(doc)}
                                disabled={busyId === doc.id}
                                className={`w-full flex items-center justify-between gap-2 text-left rounded-xl px-3 py-2.5 border transition disabled:opacity-60 ${
                                    selected
                                        ? "bg-orange-500/10 border-orange-500/40"
                                        : "bg-black border-white/10 hover:border-white/20"
                                }`}
                            >
                                <div className="min-w-0 flex items-center gap-2">
                                    <FileText size={13} className={selected ? "text-orange-400" : "text-gray-500"} />
                                    <span className="text-xs text-white truncate">{doc.file_name}</span>
                                    {doc.document_type && (
                                        <span className="text-[10px] text-gray-500 shrink-0">· {doc.document_type}</span>
                                    )}
                                </div>
                                {busyId === doc.id ? (
                                    <Loader2 size={13} className="animate-spin text-gray-400 shrink-0" />
                                ) : selected ? (
                                    <span className="flex items-center gap-1 text-[10px] font-semibold text-orange-400 shrink-0">
                                        <Check size={12} /> Selected
                                    </span>
                                ) : (
                                    <span className="text-[10px] font-semibold text-gray-500 shrink-0">Add</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ==========================================================
   Test Agent — mini chat that calls /voice-agents/{id}/test-message,
   which builds the EXACT same system prompt (product + greeting +
   tone + qualification questions + objections + selected Knowledge
   Base docs) a real call would use, then asks Groq/Gemini for one
   reply. Nothing here touches /ai-assistant/chat.
========================================================== */
const TestAgentPanel = ({ agentId, productId, productName }) => {
    const [testMessages, setTestMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);

    // ── Real Twilio test call — STEP 1: places an actual call that plays
    // a fixed greeting, to confirm the Twilio pipeline itself works
    // before the AI conversation loop is wired in on top of it (see
    // POST /voice-call/start in main.py). Deliberately separate from the
    // text-chat test above it. ──
    //
    // Leads shown here come from GET /leads?product_id=..., NOT the
    // shared useCustomerData() context — that context's `leads` are
    // every lead for the company, not just the ones for the product
    // this agent is attached to. Scoping to the company itself still
    // happens server-side via get_current_company on that endpoint.
    const [productLeads, setProductLeads] = useState([]);
    const [loadingLeads, setLoadingLeads] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState("");
    const [callState, setCallState] = useState("idle"); // idle | calling | started | error
    const [callResult, setCallResult] = useState(null);
    const [noNumberNotice, setNoNumberNotice] = useState(null); // lead name that has no number on file, or null

    useEffect(() => {
        let cancelled = false;
        setSelectedLeadId("");
        setCallState("idle");
        setCallResult(null);
        setNoNumberNotice(null);
        setLoadingLeads(true);
        fetch(leadsByProductEndpoint(productId), { headers: authHeaders() })
            .then((res) => res.json().catch(() => null))
            .then((data) => {
                if (cancelled) return;
                setProductLeads(data?.success ? data.data || [] : []);
            })
            .catch(() => {
                if (!cancelled) setProductLeads([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingLeads(false);
            });
        return () => {
            cancelled = true;
        };
    }, [productId]);

    const selectedLead = productLeads.find((l) => String(l.id) === String(selectedLeadId));

    const handleLeadSelect = (value) => {
        setCallResult(null);
        const lead = productLeads.find((l) => String(l.id) === String(value));
        if (lead && !(lead.user_mobile_number || "").trim()) {
            // Selected lead has no phone number on file — surface it
            // immediately instead of letting them hit Call and only
            // find out from the server's 400 ("no usable mobile number").
            setSelectedLeadId("");
            setNoNumberNotice(lead.user_name || "This lead");
            return;
        }
        setSelectedLeadId(value);
    };

    const startRealCall = async () => {
        if (!selectedLeadId || callState === "calling") return;
        setCallState("calling");
        setCallResult(null);
        try {
            const res = await fetch(VOICE_CALL_START_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ lead_id: Number(selectedLeadId), agent_id: agentId }),
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                setCallState("started");
                setCallResult(`Call placed (status: ${data.status || "initiated"}).`);
            } else if (res.status === 400 && /mobile number/i.test(data?.message || "")) {
                // Belt-and-suspenders: number could've gone stale between
                // fetch and click. Same popup as the dropdown-select case.
                setCallState("idle");
                setSelectedLeadId("");
                setNoNumberNotice(selectedLead?.user_name || "This lead");
            } else {
                setCallState("error");
                setCallResult(data?.message || "Couldn't start the call.");
            }
        } catch (err) {
            setCallState("error");
            setCallResult("Couldn't reach the server to start the call.");
        }
    };

    const send = async () => {
        const text = input.trim();
        if (!text || sending) return;
        setInput("");
        const history = testMessages.map((m) => ({ role: m.role, content: m.content }));
        setTestMessages((prev) => [...prev, { role: "user", content: text }]);
        setSending(true);
        try {
            const res = await fetch(voiceAgentTestEndpoint(agentId), {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ message: text, history }),
            });
            const data = await res.json().catch(() => null);
            const reply = res.ok && data?.success ? data.reply : data?.message || "Couldn't reach the agent right now.";
            setTestMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        } catch (err) {
            setTestMessages((prev) => [...prev, { role: "assistant", content: "Couldn't reach the agent right now." }]);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
            <h4 className="text-sm font-bold text-white flex items-center gap-1.5 mb-3">
                <PlayCircle size={15} className="text-orange-500" /> Test Agent
            </h4>

            <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-4">
                <p className="text-xs font-semibold text-white flex items-center gap-1.5 mb-2">
                    <PhoneCall size={13} className="text-orange-500" /> Start a real test call
                </p>
                <p className="text-[11px] text-gray-500 mb-2.5">
                    Places an actual Twilio call to a lead's number. For now it just plays a fixed
                    greeting so you can confirm the call pipeline itself works.
                    {productName ? ` Showing leads for ${productName}.` : ""}
                </p>
                <div className="flex items-center gap-2">
                    <select
                        value={selectedLeadId}
                        onChange={(e) => handleLeadSelect(e.target.value)}
                        disabled={loadingLeads}
                        className="flex-1 bg-black border border-white/10 rounded-lg px-2.5 py-2 text-xs text-white outline-none disabled:opacity-50"
                    >
                        <option value="">
                            {loadingLeads
                                ? "Loading leads..."
                                : productLeads.length === 0
                                ? "No leads for this product yet"
                                : "Select a lead to call..."}
                        </option>
                        {productLeads.map((l) => {
                            const hasNumber = (l.user_mobile_number || "").trim();
                            return (
                                <option key={l.id} value={l.id}>
                                    {l.user_name || "Unnamed"}
                                    {hasNumber ? ` — ${l.user_mobile_number}` : " — no number on file"}
                                </option>
                            );
                        })}
                    </select>
                    <button
                        onClick={startRealCall}
                        disabled={!selectedLeadId || callState === "calling"}
                        className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-black text-xs font-bold px-3 py-2 rounded-lg transition shrink-0"
                    >
                        {callState === "calling" ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <Phone size={13} />
                        )}
                        Call
                    </button>
                </div>
                {callResult && (
                    <p className={`text-[11px] mt-2 ${callState === "error" ? "text-red-400" : "text-green-400"}`}>
                        {callResult}
                    </p>
                )}
            </div>

            {noNumberNotice && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
                    onClick={() => setNoNumberNotice(null)}
                >
                    <div
                        className="bg-[#111] border border-white/10 rounded-2xl p-5 max-w-xs w-full text-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mx-auto mb-3 h-9 w-9 rounded-full bg-red-500/10 flex items-center justify-center">
                            <AlertTriangle size={17} className="text-red-400" />
                        </div>
                        <p className="text-sm font-semibold text-white mb-1">Number is not available</p>
                        <p className="text-[11px] text-gray-400 mb-4">
                            {noNumberNotice} has no phone number on file, so a call can't be made to them.
                        </p>
                        <button
                            onClick={() => setNoNumberNotice(null)}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-black text-xs font-bold px-3 py-2 rounded-lg transition"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}

            <div className={`bg-black border border-white/10 rounded-xl p-3 h-56 overflow-y-auto space-y-2.5 ${THEMED_SCROLLBAR}`}>
                {testMessages.length === 0 ? (
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <MessageCircle size={13} /> Send a message to preview exactly how this agent would respond on
                        a real call.
                    </p>
                ) : (
                    testMessages.map((m, i) => (
                        <div
                            key={i}
                            className={`text-xs leading-relaxed rounded-xl px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
                                m.role === "user"
                                    ? "bg-orange-500 text-black ml-auto w-fit"
                                    : "bg-white/5 border border-white/10 text-gray-200 w-fit"
                            }`}
                        >
                            {m.content}
                        </div>
                    ))
                )}
                {sending && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Loader2 size={12} className="animate-spin" /> Thinking...
                    </div>
                )}
            </div>

            <div className="flex items-center gap-2 mt-3">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Type a message as if you were the lead..."
                    className="flex-1 min-w-0 bg-black border border-white/10 focus:border-orange-500/50 rounded-lg px-3 py-2 text-sm text-white outline-none"
                />
                <button
                    onClick={send}
                    disabled={sending || !input.trim()}
                    className="h-9 w-9 rounded-lg bg-orange-500 disabled:bg-orange-500/30 disabled:cursor-not-allowed hover:bg-orange-600 flex items-center justify-center shrink-0 transition"
                >
                    <Send size={14} className="text-black" />
                </button>
            </div>
        </div>
    );
};

/* ==========================================================
   Chat message renderer — handles plain text plus the two
   structured, real-data message kinds (hot_leads, approval).
========================================================== */
const ChatMessage = ({ message, onApprove, onApproveGoal, onApproveCalls, onCreateAgents }) => {
    if (message.role === "user") {
        return (
            <div className="bg-orange-500 text-black ml-auto rounded-2xl p-4 max-w-[85%] text-sm leading-6 whitespace-pre-wrap break-words w-fit">
                {message.content}
            </div>
        );
    }

    if (message.kind === "hot_leads") {
        return <HotLeadsCard leads={message.leads} label={message.label} />;
    }

    if (message.kind === "approval") {
        return <ApprovalCard message={message} onApprove={onApprove} />;
    }

    if (message.kind === "agent_missing") {
        return <AgentMissingCard message={message} onCreate={onCreateAgents} />;
    }

    if (message.kind === "call_plan") {
        return <CallPlanCard message={message} onApprove={onApproveCalls} />;
    }

    if (message.kind === "goal_plan") {
        return <GoalPlanCard message={message} onApprove={onApproveGoal} />;
    }

    if (message.kind === "agent_result") {
        return <AgentResultCard message={message} />;
    }

    return (
        <div
            className={`rounded-2xl p-4 max-w-[85%] text-sm leading-6 whitespace-pre-wrap break-words bg-black border ${
                message.isError ? "border-red-500/40" : "border-orange-600/30"
            } text-gray-200`}
        >
            {message.content}
        </div>
    );
};

const HotLeadsCard = ({ leads, label = "hot lead" }) => (
    <div className="bg-black border border-orange-600/30 rounded-2xl p-4 max-w-[95%] w-fit">
        <p className="text-sm text-white font-semibold flex items-center gap-1.5 mb-3">
            <Flame size={14} className="text-orange-500" />
            {leads.length === 0 ? `No ${label}s right now` : `${leads.length} ${label}${leads.length !== 1 ? "s" : ""}`}
        </p>
        {leads.length > 0 && (
            <div className={`overflow-x-auto ${THEMED_SCROLLBAR}`}>
                <table className="text-xs w-full">
                    <thead>
                        <tr className="text-left text-gray-500 uppercase text-[10px] border-b border-white/10">
                            <th className="pb-2 pr-4 font-semibold">Lead</th>
                            <th className="pb-2 pr-4 font-semibold">Email</th>
                            <th className="pb-2 pr-4 font-semibold">AI Score</th>
                            <th className="pb-2 pr-4 font-semibold">Source</th>
                            <th className="pb-2 pr-4 font-semibold">Status</th>
                            <th className="pb-2 font-semibold">Recommended</th>
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((l) => (
                            <tr key={l.id} className="border-b border-white/5 last:border-0">
                                <td className="py-2 pr-4">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-400 text-[9px] font-bold flex items-center justify-center shrink-0">
                                            {initials(l.user_name)}
                                        </div>
                                        <span className="text-white font-medium whitespace-nowrap">{l.user_name || "Unnamed"}</span>
                                    </div>
                                </td>
                                <td className="py-2 pr-4 text-gray-300 whitespace-nowrap">{l.user_email || "—"}</td>
                                <td className="py-2 pr-4 text-orange-400 font-semibold">{l.score}</td>
                                <td className="py-2 pr-4 text-gray-300 whitespace-nowrap">{l.lead_source || "—"}</td>
                                <td className="py-2 pr-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                        l.is_contacted
                                            ? "bg-green-500/10 text-green-400 border-green-500/30"
                                            : "bg-red-500/10 text-red-400 border-red-500/30"
                                    }`}>
                                        {l.is_contacted ? "Contacted" : "Due"}
                                    </span>
                                </td>
                                <td className="py-2 text-gray-300 whitespace-nowrap">{recommendedAction(l)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

const AgentMissingCard = ({ message, onCreate }) => {
    const { products: missingProducts, status, error } = message;
    const names = missingProducts.map((p) => p.name).join(", ");

    return (
        <div className="bg-black border border-orange-600/30 rounded-2xl p-4 max-w-[95%] w-fit">
            <p className="text-sm text-white flex items-center gap-1.5">
                <AlertTriangle size={14} className="text-orange-500" />
                No voice agent exists yet for <span className="font-semibold">{names}</span>
            </p>
            {status === "done" ? (
                <p className="text-xs text-green-400 mt-2 flex items-center gap-1.5">
                    <Check size={13} /> Voice agent created with AI — preparing your call plan below
                </p>
            ) : (
                <>
                    <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
                        I can create one with AI first — its name, greeting, objective and call script drafted from your product details
                        (you can edit it later in the Voice Agents tab). Create it and continue to the call?
                    </p>
                    {status === "error" && <p className="text-[11px] text-red-400 mt-2">{error}</p>}
                    <div className="flex items-center gap-2.5 mt-3.5">
                        <button
                            onClick={onCreate}
                            disabled={status === "creating"}
                            className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black text-xs font-bold px-4 py-2 rounded-lg transition"
                        >
                            {status === "creating" ? (
                                <>
                                    <Loader2 size={13} className="animate-spin" /> Creating...
                                </>
                            ) : (
                                <>
                                    <Check size={13} /> Create with AI &amp; continue
                                </>
                            )}
                        </button>
                        {status !== "creating" && <span className="text-[11px] text-gray-500">No call is placed yet</span>}
                    </div>
                </>
            )}
        </div>
    );
};

const CallPlanCard = ({ message, onApprove }) => {
    const { calls, status, results = [], skipped = [], overCap = 0, extras } = message;
    const resultFor = (id) => results.find((r) => r.leadId === id);
    const plural = calls.length !== 1 ? "s" : "";

    return (
        <div className="bg-black border border-orange-600/30 rounded-2xl p-4 max-w-[95%] w-fit">
            <p className="text-sm text-white flex items-center gap-1.5">
                <PhoneCall size={14} className="text-orange-500" />
                {status === "done"
                    ? `Placed ${results.filter((r) => r.ok).length} of ${calls.length} call${plural}`
                    : `I can call ${calls.length} lead${plural} with your AI voice agent`}
            </p>

            <div className={`mt-2.5 space-y-2 max-h-56 overflow-y-auto pr-1 ${THEMED_SCROLLBAR}`}>
                {calls.map((c) => {
                    const r = resultFor(c.leadId);
                    return (
                        <div key={c.leadId} className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                            <p className="text-xs font-semibold text-white">
                                {c.name} <span className="text-gray-400 font-normal">· {c.phone}</span>
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">
                                Product: {c.productName || "not set"}
                                {c.productInfo ? ` (${c.productInfo})` : ""} · Agent: {c.agentName}
                            </p>
                            {r && <p className={`text-[11px] mt-1 ${r.ok ? "text-green-400" : "text-red-400"}`}>{r.text}</p>}
                        </div>
                    );
                })}
            </div>

            {extras && status !== "done" && (
                <div className="mt-2.5 bg-orange-500/5 border border-orange-600/20 rounded-xl p-2.5">
                    <p className="text-[11px] font-semibold text-orange-400 mb-1">Extra points the agent may mention (AI-generated)</p>
                    <p className="text-[11px] text-gray-300 whitespace-pre-wrap leading-relaxed">{extras}</p>
                </div>
            )}
            {skipped.length > 0 && (
                <p className="text-[11px] text-red-400 mt-2">No phone number on file, skipped: {skipped.join(", ")}</p>
            )}
            {overCap > 0 && (
                <p className="text-[11px] text-gray-500 mt-1">Batch limit reached — {overCap} more lead{overCap !== 1 ? "s" : ""} not included.</p>
            )}

            {status !== "done" && (
                <div className="flex items-center gap-2.5 mt-3.5">
                    <button
                        onClick={onApprove}
                        disabled={status === "calling"}
                        className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black text-xs font-bold px-4 py-2 rounded-lg transition"
                    >
                        {status === "calling" ? (
                            <>
                                <Loader2 size={13} className="animate-spin" /> Calling...
                            </>
                        ) : (
                            <>
                                <PhoneCall size={13} /> Approve &amp; Call
                            </>
                        )}
                    </button>
                    {status !== "calling" && (
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                            <AlertTriangle size={11} /> No call is placed until you approve
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

const ApprovalCard = ({ message, onApprove }) => {
    const [expanded, setExpanded] = useState(false);
    const { drafts, status, sentCount } = message;

    return (
        <div className="bg-black border border-orange-600/30 rounded-2xl p-4 max-w-[95%] w-fit">
            {status === "done" ? (
                <div className="flex items-center gap-2 text-sm text-green-400 font-semibold">
                    <Check size={15} /> Sent to {sentCount} of {drafts.length} leads
                </div>
            ) : (
                <>
                    <p className="text-sm text-white">
                        I've prepared follow-up emails for <span className="font-semibold">{drafts.length} leads</span> who
                        haven't been contacted yet.
                    </p>

                    <button
                        onClick={() => setExpanded((v) => !v)}
                        className="flex items-center gap-1 text-orange-400 text-xs font-semibold mt-2.5"
                    >
                        {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        Review Messages
                    </button>

                    {expanded && (
                        <div className={`mt-2.5 space-y-2 max-h-56 overflow-y-auto pr-1 ${THEMED_SCROLLBAR}`}>
                            {drafts.map((d) => (
                                <div key={d.id} className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                                    <p className="text-xs font-semibold text-white">
                                        {d.name} {!d.email && <span className="text-red-400 font-normal">— no email on file, will be skipped</span>}
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-1 whitespace-pre-wrap leading-relaxed">{d.message}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-2.5 mt-3.5">
                        <button
                            onClick={onApprove}
                            disabled={status === "sending"}
                            className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black text-xs font-bold px-4 py-2 rounded-lg transition"
                        >
                            {status === "sending" ? (
                                <>
                                    <Loader2 size={13} className="animate-spin" /> Sending...
                                </>
                            ) : (
                                <>
                                    <Check size={13} /> Approve &amp; Send
                                </>
                            )}
                        </button>
                        {status !== "sending" && (
                            <span className="text-[11px] text-gray-500 flex items-center gap-1">
                                <AlertTriangle size={11} /> Nothing sends until you approve
                            </span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

/* ==========================================================
   Goal -> Plan -> Approve -> Execute card (Phase 1 of the
   Copilot roadmap). Findings and steps are computed from real
   lead data (see startGoal in the parent) — never invented.
========================================================== */
const GoalPlanCard = ({ message, onApprove }) => {
    const [expanded, setExpanded] = useState(false);
    const { goal, findings, routedVia, steps, drafts, status, sentCount } = message;

    return (
        <div className="bg-black border border-orange-600/30 rounded-2xl p-4 max-w-[95%] w-fit">
            <p className="text-sm text-white font-semibold flex items-center gap-1.5">
                <Target size={14} className="text-orange-500" /> I found a plan for "{goal}"
            </p>

            {routedVia?.length > 0 && (
                <div className="flex items-center flex-wrap gap-1.5 mt-2">
                    <span className="text-[10px] text-gray-500">Orchestrator routed to:</span>
                    {routedVia.map((name) => (
                        <span key={name} className="text-[10px] font-semibold text-orange-400 bg-orange-500/10 border border-orange-500/30 rounded-full px-2 py-0.5">
                            {name}
                        </span>
                    ))}
                </div>
            )}

            <div className="mt-3 space-y-1.5">
                {findings.map((f, i) => (
                    <p key={i} className="text-xs text-gray-300 flex items-center gap-1.5">
                        <Check size={12} className="text-green-400 shrink-0" /> {f}
                    </p>
                ))}
            </div>

            <p className="text-xs text-gray-500 font-semibold mt-3.5 mb-1.5">Recommended Plan</p>
            <div className="space-y-1.5">
                {steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                        {s.done ? (
                            <CheckCircle2 size={13} className="text-green-400 shrink-0" />
                        ) : (
                            <Circle size={13} className="text-gray-600 shrink-0" />
                        )}
                        <span className={s.done ? "text-gray-400 line-through" : "text-gray-200"}>{s.label}</span>
                    </div>
                ))}
            </div>

            {status !== "done" && (
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="flex items-center gap-1 text-orange-400 text-xs font-semibold mt-3"
                >
                    {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    Preview {drafts.length} draft message{drafts.length !== 1 ? "s" : ""}
                </button>
            )}

            {expanded && status !== "done" && (
                <div className={`mt-2 space-y-2 max-h-48 overflow-y-auto pr-1 ${THEMED_SCROLLBAR}`}>
                    {drafts.map((d) => (
                        <div key={d.id} className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                            <p className="text-xs font-semibold text-white">
                                {d.name} {!d.email && <span className="text-red-400 font-normal">— no email on file, will be skipped</span>}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1 whitespace-pre-wrap leading-relaxed">{d.message}</p>
                        </div>
                    ))}
                </div>
            )}

            {status === "done" ? (
                <div className="flex items-center gap-2 text-sm text-green-400 font-semibold mt-3.5">
                    <Check size={15} /> Done — sent to {sentCount} of {drafts.length} leads, CRM updated
                </div>
            ) : (
                <div className="flex items-center gap-2.5 mt-3.5">
                    <button
                        onClick={onApprove}
                        disabled={status === "executing"}
                        className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black text-xs font-bold px-4 py-2 rounded-lg transition"
                    >
                        {status === "executing" ? (
                            <>
                                <Loader2 size={13} className="animate-spin" /> Executing...
                            </>
                        ) : (
                            <>
                                <Check size={13} /> Approve
                            </>
                        )}
                    </button>
                    {status !== "executing" && (
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                            <Pencil size={11} /> Edit the drafts above before approving if needed
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

/* ==========================================================
   Agent result card — shows which agent answered + its
   real-data-grounded response.
========================================================== */
const AgentResultCard = ({ message }) => {
    const { agent, content } = message;
    const Icon = agent.icon;
    return (
        <div className="bg-black border border-orange-600/30 rounded-2xl p-4 max-w-[85%] w-fit">
            <p className="text-sm text-white font-semibold flex items-center gap-2 mb-2.5">
                <Chip color={agent.color} icon={Icon} size={13} />
                {agent.label}
            </p>
            <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{content}</p>
        </div>
    );
};

export default AIAssistantSection;